import React from "react";
import WebProfitSpinner from "../../system/WebProfitSpinner";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import { useNavigate } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { fiscalApi } from "../../../api/fiscal";
import { productsApi } from "../../../api/products";
import { customersApi } from "../../../api/customers";
import {
  CASH_REGISTER_KIND_LABELS,
  ERP_BRAND,
  cashRegisterNeedsSession,
  entityId,
  formatMoney,
  paymentMethodForCashRegister,
  projectProductCode,
  projectProductName,
  projectProductPrice,
  projectProductStock,
  safeNumber,
  toLocalDateTimeInput,
  uniqueRequestKey,
} from "../../../utils/erp";
import { hasProjectPermission, hasProjectRole } from "../../../utils/projectAccess";
import BarcodeScannerDialog from "../items/BarcodeScannerDialog";

const KIND_OPTIONS = [
  { value: "sale", label: "Vendí algo", detail: "Producto o servicio", permission: "erp.sales.create" },
  { value: "purchase", label: "Compré mercadería", detail: "Registra la compra; el stock entra cuando llega", permission: "erp.purchases.create" },
  { value: "expense", label: "Pagué un gasto", detail: "Servicio, alquiler o insumo", permission: "erp.expenses.create" },
  { value: "collection", label: "Cobré una deuda", detail: "Un cliente me pagó", permission: "erp.collections.create" },
  { value: "payment", label: "Pagué una deuda", detail: "Le pagué a un proveedor", permission: "erp.payments.create" },
  { value: "transfer", label: "Moví dinero", detail: "Entre dos cajas o cuentas", permission: "erp.payments.create" },
  { value: "cash_in", label: "Ingresó dinero", detail: "Sin venta asociada", permission: "cash.operations.in", legacyPermission: "cash.operations.create" },
  { value: "cash_out", label: "Retiré dinero", detail: "Uso personal u otro motivo", permission: "cash.operations.out", legacyPermission: "cash.operations.create" },
  { value: "opening_balance", label: "Cargué stock que ya tenía", detail: "Inventario inicial, sin mover dinero", permission: "erp.stock.manage" },
  { value: "stock_in", label: "Ingresar stock", detail: "Entrada manual sin compra ni pago", permission: "erp.stock.manage" },
  { value: "stock_out", label: "Descontar stock", detail: "Salida manual sin venta ni cobro", permission: "erp.stock.manage" },
  { value: "stock_adjustment", label: "Corregir stock", detail: "Conteo, rotura o diferencia", permission: "erp.stock.manage" },
];

const CATALOG_STOCK_KIND_OPTIONS = [
  {
    value: "opening_balance",
    label: "Ya lo tenía",
    title: "Stock inicial",
    detail: "Mercadería que ya estaba en tu negocio antes de empezar a usar WebProfit.",
    hint: "Suma unidades y valor de inventario. No mueve plata.",
    icon: Inventory2RoundedIcon,
  },
  {
    value: "purchase",
    label: "Lo compré",
    title: "Compra reciente",
    detail: "Compraste esta mercadería y querés registrar también proveedor, factura y pago.",
    hint: "Puede quedar por pagar o sin número de factura si todavía no lo tenés.",
    icon: ShoppingCartCheckoutRoundedIcon,
  },
  {
    value: "stock_in",
    label: "Sólo ingresó",
    title: "Ingreso sin compra",
    detail: "Las unidades entraron, pero no querés registrar una compra ni movimiento de dinero.",
    hint: "Útil si no tenés comprobante, fue una devolución, regalo o regularización simple.",
    icon: AddBoxRoundedIcon,
  },
  {
    value: "stock_adjustment",
    label: "Lo conté",
    title: "Ajustar existencia",
    detail: "El stock real no coincide con el sistema y querés corregir la diferencia.",
    hint: "Deja trazabilidad del ajuste sin inventar una compra o venta.",
    icon: TuneRoundedIcon,
  },
];

const ITEM_KINDS = new Set(["sale", "purchase", "expense", "opening_balance", "stock_in", "stock_out", "stock_adjustment"]);
const COMMERCIAL_KINDS = new Set(["sale", "purchase", "expense"]);
const MANUAL_MONEY_KINDS = new Set(["collection", "payment", "cash_in", "cash_out"]);
const VARIANT_DISTRIBUTION_KINDS = new Set(["purchase", "opening_balance", "stock_in", "stock_out"]);
const PRODUCT_SEARCH_PAGE_SIZE = 25;
const PRODUCT_SEARCH_DEBOUNCE_MS = 300;

function emptyItem() {
  return {
    productId: "",
    variantId: "",
    name: "",
    code: "",
    itemType: "product",
    quantity: "1",
    adjustmentDelta: "0",
    unitPrice: "0",
    unitCost: "0",
    discountAmount: "0",
    taxAmount: "0",
    tracksStock: false,
    stockReceiptPending: false,
    stockIdentificationPending: false,
    variantQuantities: {},
    variantReceiptPending: {},
    accountingAccountCode: "",
  };
}

function emptyPayment() {
  return {
    amount: "0",
    cashRegisterId: "",
    reference: "",
    installments: "1",
    commissionAmount: "",
    commissionTaxAmount: "",
    financingCostAmount: "",
    withholdingAmount: "",
    providerPaymentId: "",
    settlementSource: "",
    settlementStatus: "",
    settlementNetAmount: "",
    providerGrossAmount: "",
  };
}

function registerInstallmentPercent(register = {}, installments = 1) {
  const rows = register.paymentProcessing?.installmentFees || [];
  const row = rows.find((item) => Number(item.installments) === Number(installments));
  return safeNumber(row?.percent);
}

function paymentSettlementPreview(register = {}, payment = {}) {
  const amount = Math.max(0, safeNumber(payment.amount));
  const processing = register.paymentProcessing || {};
  const provider = processing.provider || "none";
  const installments = Math.max(1, Number(payment.installments || 1));
  if (provider === "mercadopago" && payment.settlementSource === "provider") {
    const commissionAmount = safeNumber(payment.commissionAmount);
    const commissionTaxAmount = safeNumber(payment.commissionTaxAmount);
    const financingCostAmount = safeNumber(payment.financingCostAmount);
    const withholdingAmount = safeNumber(payment.withholdingAmount);
    const totalCost = roundPaymentAmount(commissionAmount + commissionTaxAmount + financingCostAmount + withholdingAmount);
    const providerNet = safeNumber(payment.settlementNetAmount);
    return {
      provider,
      installments,
      commissionAmount,
      commissionTaxAmount,
      financingCostAmount,
      withholdingAmount,
      totalCost,
      netAmount: providerNet > 0 ? providerNet : roundPaymentAmount(Math.max(0, amount - totalCost)),
    };
  }
  if (provider !== "manual" || processing.deductsFees !== true) {
    return { provider, installments, commissionAmount: 0, commissionTaxAmount: 0, financingCostAmount: 0, withholdingAmount: 0, totalCost: 0, netAmount: amount };
  }
  const defaultCommission = amount * (safeNumber(processing.commissionPercent) / 100) + safeNumber(processing.commissionFixed);
  const commissionAmount = payment.commissionAmount === "" ? defaultCommission : safeNumber(payment.commissionAmount);
  const defaultCommissionTax = commissionAmount * (safeNumber(processing.commissionTaxPercent) / 100);
  const commissionTaxAmount = payment.commissionTaxAmount === "" ? defaultCommissionTax : safeNumber(payment.commissionTaxAmount);
  const defaultFinancing = amount * (registerInstallmentPercent(register, installments) / 100);
  const financingCostAmount = payment.financingCostAmount === "" ? defaultFinancing : safeNumber(payment.financingCostAmount);
  const withholdingAmount = safeNumber(payment.withholdingAmount);
  const totalCost = roundPaymentAmount(commissionAmount + commissionTaxAmount + financingCostAmount + withholdingAmount);
  return {
    provider,
    installments,
    commissionAmount: roundPaymentAmount(commissionAmount),
    commissionTaxAmount: roundPaymentAmount(commissionTaxAmount),
    financingCostAmount: roundPaymentAmount(financingCostAmount),
    withholdingAmount: roundPaymentAmount(withholdingAmount),
    totalCost,
    netAmount: roundPaymentAmount(Math.max(0, amount - totalCost)),
  };
}

function emptyForm() {
  return {
    kind: "sale",
    occurredAt: toLocalDateTimeInput(),
    description: "",
    notes: "",
    sourceNumber: "",
    partyType: "customer",
    partyId: "",
    partyName: "",
    partyDocument: "",
    partyEmail: "",
    partyPhone: "",
    items: [emptyItem()],
    payments: [],
    manualAmount: "",
    moneyRegisterId: "",
    paymentReference: "",
    transferFromId: "",
    transferToId: "",
    idempotencyKey: uniqueRequestKey("movement-ui"),
  };
}

function productVariants(product) {
  const source = product?.variants || product?.variations || [];
  return (Array.isArray(source) ? source : []).filter((variant) => variant?.active !== false);
}

function variantIdOf(variant) {
  return entityId(variant);
}

function variantLabelOf(product, variant) {
  const axes = product?.variantAttributes || product?.variantOptions || [];
  return (Array.isArray(axes) ? axes : [])
    .map((axis) => variant?.attributes?.[axis.key])
    .filter(Boolean)
    .join(" / ");
}

function variantPriceOf(product, variant) {
  const ownPrice = variant?.price;
  return ownPrice === null || ownPrice === undefined || ownPrice === ""
    ? projectProductPrice(product)
    : safeNumber(ownPrice);
}

function distributedQuantity(item) {
  const allocations = item?.variantQuantities && typeof item.variantQuantities === "object"
    ? Object.values(item.variantQuantities)
    : [];
  if (!allocations.length) return Math.max(0, safeNumber(item?.quantity));
  return allocations.reduce((sum, value) => sum + Math.max(0, safeNumber(value)), 0);
}

function lineTotal(item, kind) {
  if (kind === "stock_adjustment") return 0;
  const quantity = distributedQuantity(item);
  const unitValue = ["purchase", "expense", "opening_balance"].includes(kind)
    ? safeNumber(item.unitCost || item.unitPrice)
    : safeNumber(item.unitPrice);
  return Math.max(0, quantity * unitValue - safeNumber(item.discountAmount) + safeNumber(item.taxAmount));
}

const PAYMENT_TOTAL_SYNC_EPSILON = 0.005;

function roundPaymentAmount(value) {
  return Math.round((safeNumber(value) + Number.EPSILON) * 100) / 100;
}

function syncSettledPaymentsToTotal(payments, previousTotal, nextTotal) {
  const source = Array.isArray(payments) ? payments : [];
  if (!source.length) return source;

  const previous = roundPaymentAmount(previousTotal);
  const next = roundPaymentAmount(nextTotal);
  const paid = roundPaymentAmount(source.reduce((sum, payment) => sum + safeNumber(payment.amount), 0));

  // Only keep the quote automatic while the movement was fully settled before
  // the item edit. Partial/manual payments must remain untouched on purpose.
  if (Math.abs(paid - previous) > PAYMENT_TOTAL_SYNC_EPSILON) return source;

  const delta = roundPaymentAmount(next - paid);
  if (Math.abs(delta) <= PAYMENT_TOTAL_SYNC_EPSILON) return source;

  const adjusted = source.map((payment) => ({ ...payment }));
  if (delta > 0) {
    let targetIndex = -1;
    for (let index = adjusted.length - 1; index >= 0; index -= 1) {
      if (adjusted[index].cashRegisterId || safeNumber(adjusted[index].amount) > 0) {
        targetIndex = index;
        break;
      }
    }
    if (targetIndex < 0) targetIndex = adjusted.length - 1;
    adjusted[targetIndex].amount = String(roundPaymentAmount(
      safeNumber(adjusted[targetIndex].amount) + delta,
    ));
    return adjusted;
  }

  let remainingReduction = roundPaymentAmount(-delta);
  for (let index = adjusted.length - 1; index >= 0 && remainingReduction > PAYMENT_TOTAL_SYNC_EPSILON; index -= 1) {
    const currentAmount = roundPaymentAmount(adjusted[index].amount);
    if (currentAmount <= 0) continue;
    const reduction = Math.min(currentAmount, remainingReduction);
    adjusted[index].amount = String(roundPaymentAmount(currentAmount - reduction));
    remainingReduction = roundPaymentAmount(remainingReduction - reduction);
  }
  return adjusted;
}

function movementItemAccountCode(kind, item = {}) {
  if (kind === "sale") return item.salesAccountCodeSnapshot || "";
  if (kind === "purchase") {
    return item.tracksStockSnapshot
      ? item.inventoryAccountCodeSnapshot || ""
      : item.purchaseAccountCodeSnapshot || "";
  }
  if (kind === "expense") return item.expenseAccountCodeSnapshot || "";
  if (kind === "opening_balance") return item.inventoryAccountCodeSnapshot || "";
  return "";
}

function normalizeExisting(movement) {
  if (!movement) return emptyForm();
  const payments = (movement.payments || []).map((payment) => ({
    amount: String(payment.amount || 0),
    cashRegisterId: entityId(payment.cashRegisterId),
    reference: payment.reference || "",
    installments: String(payment.settlement?.installments || 1),
    commissionAmount: payment.settlement?.commissionAmount ? String(payment.settlement.commissionAmount) : "",
    commissionTaxAmount: payment.settlement?.commissionTaxAmount ? String(payment.settlement.commissionTaxAmount) : "",
    financingCostAmount: payment.settlement?.financingCostAmount ? String(payment.settlement.financingCostAmount) : "",
    withholdingAmount: payment.settlement?.withholdingAmount ? String(payment.settlement.withholdingAmount) : "",
    providerPaymentId: payment.settlement?.providerPaymentId || "",
    settlementSource: payment.settlement?.source || "",
    settlementStatus: payment.settlement?.status || "",
    settlementNetAmount: payment.settlement?.netAmount ? String(payment.settlement.netAmount) : "",
    providerGrossAmount: payment.settlement?.providerGrossAmount ? String(payment.settlement.providerGrossAmount) : "",
  }));
  const firstPayment = payments[0] || emptyPayment();
  return {
    ...emptyForm(),
    kind: movement.kind || "sale",
    occurredAt: toLocalDateTimeInput(movement.occurredAt),
    description: movement.description || "",
    notes: movement.notes || "",
    sourceNumber: movement.sourceNumber || "",
    partyType: movement.party?.type || "other",
    partyId: entityId(movement.party?.partyId),
    partyName: movement.party?.name || "",
    partyDocument: movement.party?.document || "",
    partyEmail: movement.party?.email || "",
    partyPhone: movement.party?.phone || "",
    items: (movement.items || []).length
      ? movement.items.map((item) => ({
          productId: entityId(item.productId),
          variantId: entityId(item.variantId),
          name: item.nameSnapshot || "",
          code: item.codeSnapshot || "",
          itemType: item.itemTypeSnapshot || "product",
          quantity: String(item.quantity || 1),
          adjustmentDelta: String(item.stockDelta ?? 0),
          unitPrice: String(item.unitPrice || 0),
          unitCost: String(item.unitCost || 0),
          discountAmount: String(item.discountAmount || 0),
          taxAmount: String(item.taxAmount || 0),
          tracksStock: Boolean(item.tracksStockSnapshot),
          stockReceiptPending: movement.kind === "purchase" && item.stockReceiptStatus === "pending",
          stockIdentificationPending: movement.kind === "purchase" && item.stockReceiptStatus === "unidentified",
          variantQuantities: item.variantId
            ? { [entityId(item.variantId)]: String(item.quantity || 1) }
            : {},
          variantReceiptPending: item.variantId && movement.kind === "purchase"
            ? { [entityId(item.variantId)]: item.stockReceiptStatus === "pending" }
            : {},
          accountingAccountCode: movementItemAccountCode(movement.kind, item),
        }))
      : [emptyItem()],
    payments: COMMERCIAL_KINDS.has(movement.kind) ? payments : [],
    manualAmount: MANUAL_MONEY_KINDS.has(movement.kind) ? String(movement.total || 0) : "",
    moneyRegisterId: MANUAL_MONEY_KINDS.has(movement.kind) ? firstPayment.cashRegisterId : "",
    paymentReference: MANUAL_MONEY_KINDS.has(movement.kind) ? firstPayment.reference : "",
    transferFromId: entityId(movement.transfer?.fromCashRegisterId),
    transferToId: entityId(movement.transfer?.toCashRegisterId),
    idempotencyKey: movement.idempotencyKey || uniqueRequestKey("movement-ui"),
  };
}

function patchFormItem(form, index, patch) {
  if (!form) return form;
  return {
    ...form,
    items: (form.items || []).map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
  };
}

function formFingerprint(form) {
  return form ? JSON.stringify(form) : "";
}

function kindAllowed(access, option) {
  return hasProjectPermission(access, option.permission)
    || (option.legacyPermission && hasProjectPermission(access, option.legacyPermission));
}

function SectionHeading({ step, title, description, action = null }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ sm: "flex-start" }}
      spacing={1.4}
      sx={{ mb: 1.8 }}
    >
      <Stack direction="row" spacing={1.2} alignItems="flex-start">
        {step ? (
          <Box
            sx={{
              width: 30,
              height: 30,
              flex: "0 0 auto",
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: ERP_BRAND.dark,
              color: "#fff",
              fontWeight: 950,
              fontSize: 13,
              boxShadow: "0 6px 16px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.18)",
            }}
          >
            {step}
          </Box>
        ) : null}
        <Box>
          <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.2 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35, lineHeight: 1.45 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {action}
    </Stack>
  );
}

function productSearchParams(kind, q = "") {
  const params = { page: 1, pageSize: PRODUCT_SEARCH_PAGE_SIZE, q: String(q || "").trim() };
  if (kind === "sale") params.sellable = true;
  if (kind === "purchase") params.purchasable = true;
  if (kind === "expense") params.expenseEnabled = true;
  if (["opening_balance", "stock_in", "stock_out", "stock_adjustment"].includes(kind)) params.stockTracked = true;
  return params;
}

function AsyncProductAutocomplete({
  enabled,
  projectId,
  kind,
  value,
  onChange,
  onProductsLoaded,
  productCapabilities,
  isProductSelectable,
  requiresProduct,
  disabled = false,
}) {
  const [options, setOptions] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState("");
  const requestSequenceRef = React.useRef(0);

  const optionLabel = React.useCallback((product) => {
    if (!product) return "";
    const name = projectProductName(product) || "Ítem";
    const code = projectProductCode(product);
    const stock = productCapabilities(product).stockTracked
      ? ` · stock ${projectProductStock(product)}`
      : "";
    return `${name}${code ? ` · ${code}` : ""}${stock}`;
  }, [productCapabilities]);

  const valueId = entityId(value);
  React.useEffect(() => {
    if (value) {
      setInputValue(optionLabel(value));
      return;
    }
    setInputValue("");
  }, [optionLabel, value, valueId]);

  React.useEffect(() => {
    setSearchTerm("");
  }, [kind, projectId]);

  React.useEffect(() => {
    if (!enabled || !projectId) {
      setLoading(false);
      return undefined;
    }
    const requestId = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestId;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setSearchError("");
      productsApi
        .list(projectId, productSearchParams(kind, searchTerm))
        .then((data) => {
          if (requestSequenceRef.current !== requestId) return;
          const loadedProducts = Array.isArray(data?.products) ? data.products : [];
          const nextOptions = loadedProducts.filter(isProductSelectable);
          setOptions(nextOptions);
          onProductsLoaded(loadedProducts);
        })
        .catch((requestError) => {
          if (requestSequenceRef.current !== requestId) return;
          setOptions([]);
          setSearchError(requestError?.message || "No se pudieron buscar los ítems.");
        })
        .finally(() => {
          if (requestSequenceRef.current === requestId) setLoading(false);
        });
    }, PRODUCT_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      if (requestSequenceRef.current === requestId) requestSequenceRef.current += 1;
    };
  }, [enabled, isProductSelectable, kind, onProductsLoaded, projectId, searchTerm]);

  const visibleOptions = React.useMemo(() => {
    if (!valueId) return options;
    return [value, ...options.filter((option) => entityId(option) !== valueId)].filter(Boolean);
  }, [options, value, valueId]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      options={visibleOptions}
      value={value || null}
      inputValue={inputValue}
      loading={loading}
      disableClearable={requiresProduct}
      filterOptions={(available) => available}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, selected) => entityId(option) === entityId(selected)}
      loadingText="Buscando ítems…"
      noOptionsText={searchError ? "No se pudo completar la búsqueda" : "Sin resultados"}
      onInputChange={(_event, nextInputValue, reason) => {
        setInputValue(nextInputValue);
        if (reason === "input" || reason === "clear") setSearchTerm(nextInputValue);
      }}
      onChange={(_event, nextValue) => {
        onChange(nextValue || null);
        setInputValue(nextValue ? optionLabel(nextValue) : "");
        setSearchTerm("");
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Ítem"
          error={Boolean(searchError)}
          helperText={searchError || (requiresProduct
            ? "Buscá por nombre o código."
            : "Buscá por nombre o código, o dejalo vacío para usar un concepto manual.")}
        />
      )}
    />
  );
}

export default function MovementFormDialog({
  open,
  onClose,
  onSaved,
  projectId,
  movement = null,
  bootstrap = { registers: [], openSessions: [], accounts: [] },
  access = {},
  initialKind = "",
  initialProductId = "",
  initialProduct = null,
  initialPreset = null,
  entryContext = "",
}) {
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const navigate = useNavigate();
  const [form, setForm] = React.useState(emptyForm);
  const baselineFormRef = React.useRef(null);
  const quotedPaymentsTotalRef = React.useRef({ formKey: "", total: 0 });
  const [discardConfirmOpen, setDiscardConfirmOpen] = React.useState(false);
  const [products, setProducts] = React.useState([]);
  const knownProductIdsRef = React.useRef(new Set());
  const initialProductAppliedRef = React.useRef("");
  const [customers, setCustomers] = React.useState([]);
  const [loadingCustomers, setLoadingCustomers] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [settlementLoadingIndex, setSettlementLoadingIndex] = React.useState(-1);
  const [draftMovement, setDraftMovement] = React.useState(null);
  const [confirmationOpen, setConfirmationOpen] = React.useState(false);
  const [fiscalAction, setFiscalAction] = React.useState("pending");
  const [fiscalRecipient, setFiscalRecipient] = React.useState({
    name: "Consumidor final",
    documentNumber: "",
    taxConditionCode: 5,
    address: "",
  });
  const [fiscalResult, setFiscalResult] = React.useState(null);
  const [scannerItemIndex, setScannerItemIndex] = React.useState(null);
  const [error, setError] = React.useState("");
  const [saveErrorDialogMessage, setSaveErrorDialogMessage] = React.useState("");
  const canReadCustomers = hasProjectPermission(access, "customers.read");
  const canManageCustomers = hasProjectPermission(access, "customers.manage");
  const canOperateFiscal = hasProjectRole(access, ["owner", "admin", "facturacion", "superadmin"]);
  const catalogStockFlow = !movement && entryContext === "catalog-stock";
  const [activeStep, setActiveStep] = React.useState(0);
  const [maxVisitedStep, setMaxVisitedStep] = React.useState(0);
  const [stepError, setStepError] = React.useState("");
  const [catalogOriginChosen, setCatalogOriginChosen] = React.useState(false);

  const registers = React.useMemo(
    () => (bootstrap.registers || []).filter((register) => register?.active !== false),
    [bootstrap.registers],
  );
  const openRegisterIds = React.useMemo(
    () => new Set((bootstrap.openSessions || []).map((session) => entityId(session.registerId))),
    [bootstrap.openSessions],
  );
  const allowedKinds = React.useMemo(
    () => KIND_OPTIONS.filter((option) => kindAllowed(access, option)),
    [access],
  );
  const allowedKindKey = React.useMemo(
    () => allowedKinds.map((option) => option.value).join("|"),
    [allowedKinds],
  );

  React.useEffect(() => {
    if (!open) return;
    const normalized = normalizeExisting(movement);
    if (!movement) {
      const allowedKindValues = allowedKindKey ? allowedKindKey.split("|") : [];
      const explicitCatalogKind = entryContext === "catalog-stock"
        && CATALOG_STOCK_KIND_OPTIONS.some((option) => option.value === initialKind)
        && allowedKindValues.includes(initialKind);
      normalized.kind = allowedKindValues.includes(initialKind)
        ? initialKind
        : entryContext === "catalog-stock" && allowedKindValues.includes("stock_in")
          ? "stock_in"
          : allowedKindValues[0] || "sale";
      if (entryContext === "catalog-stock") {
        normalized.description = normalized.kind === "purchase"
          ? "Compra de mercadería desde catálogo"
          : normalized.kind === "opening_balance"
            ? "Stock inicial desde catálogo"
            : normalized.kind === "stock_adjustment"
              ? "Ajuste de stock desde catálogo"
              : "Ingreso de stock desde catálogo";
      }
      if (initialPreset && typeof initialPreset === "object") {
        const presetAmount = Math.max(0, safeNumber(initialPreset.amount));
        const presetName = String(initialPreset.name || initialPreset.description || "").trim();
        if (presetName) normalized.description = String(initialPreset.description || presetName).trim();
        if (initialPreset.notes) normalized.notes = String(initialPreset.notes);
        if (normalized.kind === "expense" && presetName) {
          normalized.items = [{
            ...emptyItem(),
            name: presetName,
            quantity: "1",
            unitCost: presetAmount > 0 ? String(presetAmount) : "0",
            unitPrice: presetAmount > 0 ? String(presetAmount) : "0",
            accountingAccountCode: String(initialPreset.expenseAccountCode || "OTHER_EXPENSE").trim().toUpperCase(),
          }];
          normalized.payments = initialPreset.cashRegisterId && presetAmount > 0
            ? [{ ...emptyPayment(), amount: String(presetAmount), cashRegisterId: String(initialPreset.cashRegisterId) }]
            : [];
        }
      }
      setCatalogOriginChosen(Boolean(explicitCatalogKind));
    } else {
      setCatalogOriginChosen(false);
    }
    baselineFormRef.current = normalized;
    setForm(normalized);
    setDraftMovement(movement || null);
    setConfirmationOpen(false);
    setFiscalAction("pending");
    setFiscalRecipient({ name: "Consumidor final", documentNumber: "", taxConditionCode: 5, address: "" });
    setFiscalResult(null);
    setDiscardConfirmOpen(false);
    setScannerItemIndex(null);
    setActiveStep(0);
    setMaxVisitedStep(movement ? 99 : 0);
    setStepError("");
    setError("");
    setSaveErrorDialogMessage("");
  }, [allowedKindKey, entryContext, initialKind, initialPreset, movement, open]);

  React.useEffect(() => {
    if (!open || !projectId || !canReadCustomers || !["sale", "collection"].includes(form.kind)) {
      if (!open) setCustomers([]);
      return undefined;
    }
    let mounted = true;
    setLoadingCustomers(true);
    customersApi
      .list(projectId, { page: 1, pageSize: 150, status: "active", sortBy: "name", sortDir: "asc" })
      .then((data) => {
        if (mounted) setCustomers(Array.isArray(data?.customers) ? data.customers : []);
      })
      .catch((requestError) => {
        if (mounted) setError(requestError?.message || "No se pudo cargar el CRM de clientes.");
      })
      .finally(() => {
        if (mounted) setLoadingCustomers(false);
      });
    return () => { mounted = false; };
  }, [canReadCustomers, form.kind, open, projectId]);

  const usesItems = ITEM_KINDS.has(form.kind);
  const commercial = COMMERCIAL_KINDS.has(form.kind);
  const manualMoney = MANUAL_MONEY_KINDS.has(form.kind);
  const transferMode = form.kind === "transfer";
  const openingInventory = form.kind === "opening_balance";
  const stockIn = form.kind === "stock_in";
  const stockOut = form.kind === "stock_out";
  const stockAdjustment = form.kind === "stock_adjustment";
  const manualStockMove = stockIn || stockOut;
  const canChooseAccountingAccount = hasProjectPermission(access, "erp.ledger.manage");
  const requiresParty = ["sale", "purchase", "expense", "collection", "payment"].includes(form.kind);
  const customerMovement = ["sale", "collection"].includes(form.kind);
  const requiresStockProduct = openingInventory || manualStockMove || stockAdjustment;

  const hasItemValueStep = usesItems
    && ["sale", "purchase", "expense", "opening_balance"].includes(form.kind)
    && !stockAdjustment
    && !manualStockMove;
  const hasReceiptStep = form.kind === "purchase"
    && !catalogStockFlow
    && form.items.some((item) => (
      item.tracksStock
      && !item.stockIdentificationPending
      && Object.keys(item.variantQuantities || {}).length <= 1
    ));
  const hasAccountingStep = canChooseAccountingAccount
    && !catalogStockFlow
    && usesItems
    && !stockAdjustment
    && ["sale", "purchase", "expense", "opening_balance"].includes(form.kind);

  const movementSteps = React.useMemo(() => {
    if (catalogStockFlow) {
      const steps = [
        { id: "origin", label: "Origen", detail: "Qué pasó" },
        { id: "quantity", label: "Cantidad", detail: "Unidades / variantes" },
      ];
      if (hasItemValueStep) {
        steps.push({ id: "value", label: form.kind === "purchase" ? "Costo" : "Valor", detail: "Importe unitario" });
      }
      if (form.kind === "purchase") {
        steps.push({ id: "supplier", label: "Proveedor", detail: "Opcional", optional: true });
        steps.push({ id: "document", label: "Comprobante", detail: "Opcional", optional: true });
        steps.push({ id: "money", label: "Pago", detail: "Puede quedar pendiente", optional: true });
      }
      steps.push({ id: "details", label: "Detalle", detail: "Opcional", optional: true });
      steps.push({ id: "review", label: "Revisar", detail: "Antes de impactar" });
      return steps;
    }

    const steps = [
      { id: "kind", label: "Qué pasó", detail: "Elegí el caso" },
      { id: "description", label: "Descripción", detail: "Una frase" },
      { id: "timing", label: "Fecha", detail: "Cuándo pasó", optional: true },
      { id: "document", label: "Comprobante", detail: "Opcional", optional: true },
    ];
    if (requiresParty) steps.push({ id: "party", label: customerMovement ? "Cliente" : "Persona", detail: "Opcional", optional: true });
    if (usesItems) {
      steps.push({ id: "item", label: "Ítem", detail: "Cuál fue" });
      steps.push({ id: "quantity", label: "Cantidad", detail: "Cuánto" });
      if (hasItemValueStep) {
        steps.push({ id: "value", label: form.kind === "sale" ? "Precio" : "Costo", detail: "Valor unitario" });
      }
      if (hasReceiptStep) steps.push({ id: "receipt", label: "Recepción", detail: "Opcional", optional: true });
      if (hasAccountingStep) steps.push({ id: "account", label: "Cuenta", detail: "Opcional", optional: true });
    }
    if (commercial || manualMoney || transferMode) {
      steps.push({
        id: "money",
        label: commercial ? (form.kind === "sale" ? "Cobro" : "Pago") : transferMode ? "Transferir" : "Dinero",
        detail: commercial ? "Puede quedar pendiente" : "Caja / cuenta",
        optional: commercial,
      });
    }
    steps.push({ id: "notes", label: "Nota", detail: "Opcional", optional: true });
    steps.push({ id: "review", label: "Revisar", detail: "Antes de impactar" });
    return steps;
  }, [
    catalogStockFlow,
    commercial,
    customerMovement,
    form.kind,
    hasAccountingStep,
    hasItemValueStep,
    hasReceiptStep,
    manualMoney,
    requiresParty,
    transferMode,
    usesItems,
  ]);

  const safeActiveStep = Math.min(activeStep, Math.max(0, movementSteps.length - 1));
  const activeStepMeta = movementSteps[safeActiveStep] || movementSteps[0];
  const activeStepId = activeStepMeta?.id || "kind";
  const isStep = React.useCallback((stepId) => activeStepId === stepId, [activeStepId]);
  const isItemMicroStep = ["item", "quantity", "value", "receipt", "account"].includes(activeStepId);
  const stepAccessLimit = movement ? Math.max(0, movementSteps.length - 1) : Math.min(maxVisitedStep, Math.max(0, movementSteps.length - 1));

  React.useEffect(() => {
    if (safeActiveStep !== activeStep) setActiveStep(safeActiveStep);
  }, [activeStep, safeActiveStep]);

  const productCapabilities = React.useCallback((product) => {
    const operational = product?.operational || {};
    const kind = String(product?.erpItemKind || product?.itemKind || product?.itemType || product?.tipo || "product").toLowerCase();
    const defaults = {
      product: { sellable: true, purchasable: true, stockTracked: true, expenseEnabled: false },
      service: { sellable: true, purchasable: false, stockTracked: false, expenseEnabled: false },
      supply: { sellable: false, purchasable: true, stockTracked: true, expenseEnabled: true },
      expense: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true },
      asset: { sellable: false, purchasable: true, stockTracked: false, expenseEnabled: true },
      charge: { sellable: true, purchasable: true, stockTracked: false, expenseEnabled: true },
    }[kind] || {};
    return { ...defaults, ...operational, kind };
  }, []);

  const productSelectableForCurrentKind = React.useCallback((product) => {
    const capabilities = productCapabilities(product);
    if (form.kind === "sale") return capabilities.sellable === true;
    if (form.kind === "purchase") return capabilities.purchasable === true;
    if (form.kind === "expense") return capabilities.expenseEnabled === true;
    if (requiresStockProduct) return capabilities.stockTracked === true;
    return true;
  }, [form.kind, productCapabilities, requiresStockProduct]);

  const rememberProducts = React.useCallback((incoming) => {
    const nextProducts = (Array.isArray(incoming) ? incoming : [incoming]).filter(Boolean);
    if (!nextProducts.length) return;
    nextProducts.forEach((product) => {
      const id = entityId(product);
      if (id) knownProductIdsRef.current.add(id);
    });
    setProducts((current) => {
      const merged = new Map(current.map((product) => [entityId(product), product]));
      nextProducts.forEach((product) => {
        const id = entityId(product);
        if (id) merged.set(id, product);
      });
      return Array.from(merged.values());
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    knownProductIdsRef.current = new Set();
    initialProductAppliedRef.current = "";
    setProducts([]);
  }, [open, projectId]);

  const selectedProductKey = React.useMemo(() => {
    const ids = form.items.map((item) => String(item?.productId || "").trim()).filter(Boolean);
    if (!movement && initialProductId) ids.push(String(initialProductId));
    return Array.from(new Set(ids)).sort().join("|");
  }, [form.items, initialProductId, movement]);

  React.useEffect(() => {
    if (!open || !projectId || !selectedProductKey) return undefined;
    const missingIds = selectedProductKey
      .split("|")
      .filter((id) => id && !knownProductIdsRef.current.has(id));
    if (!missingIds.length) return undefined;

    let mounted = true;
    Promise.all(missingIds.map((productId) =>
      productsApi
        .list(projectId, { productId, page: 1, pageSize: 1 })
        .then((data) => (Array.isArray(data?.products) ? data.products[0] : null))
        .catch(() => null),
    )).then((loadedProducts) => {
      if (mounted) rememberProducts(loadedProducts);
    });
    return () => { mounted = false; };
  }, [open, projectId, rememberProducts, selectedProductKey]);

  const total = React.useMemo(() => {
    if (usesItems) return form.items.reduce((sum, item) => sum + lineTotal(item, form.kind), 0);
    return Math.max(0, safeNumber(form.manualAmount));
  }, [form.items, form.kind, form.manualAmount, usesItems]);
  const paid = React.useMemo(() => {
    if (manualMoney || transferMode) return total;
    return form.payments.reduce((sum, payment) => sum + safeNumber(payment.amount), 0);
  }, [form.payments, manualMoney, total, transferMode]);
  const pending = commercial ? Math.max(0, total - paid) : 0;

  React.useEffect(() => {
    const formKey = String(form.idempotencyKey || "");
    const roundedTotal = roundPaymentAmount(total);
    const previous = quotedPaymentsTotalRef.current;

    if (!open || !commercial || previous.formKey !== formKey) {
      quotedPaymentsTotalRef.current = { formKey, total: roundedTotal };
      return;
    }

    if (Math.abs(previous.total - roundedTotal) <= PAYMENT_TOTAL_SYNC_EPSILON) return;

    setForm((current) => {
      const nextPayments = syncSettledPaymentsToTotal(
        current.payments,
        previous.total,
        roundedTotal,
      );
      return nextPayments === current.payments
        ? current
        : { ...current, payments: nextPayments };
    });
    quotedPaymentsTotalRef.current = { formKey, total: roundedTotal };
  }, [commercial, form.idempotencyKey, open, total]);

  const registerById = React.useCallback(
    (id) => registers.find((register) => entityId(register) === String(id || "")),
    [registers],
  );
  const registerIsUsable = React.useCallback((register) => {
    if (!register) return false;
    if (!cashRegisterNeedsSession(register)) return true;
    return openRegisterIds.has(entityId(register));
  }, [openRegisterIds]);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const customerIdOf = (customer) => String(customer?.id || customer?._id || "").trim();
  const customerOptionLabel = (customer) => {
    const name = String(customer?.name || "Cliente").trim();
    const identity = String(customer?.document || customer?.email || customer?.phone || "").trim();
    return identity ? `${name} · ${identity}` : name;
  };
  const customerSnapshot = (customer) => ({
    partyId: customerIdOf(customer),
    partyType: "customer",
    partyName: String(customer?.name || "").trim(),
    partyDocument: String(customer?.document || "").trim(),
    partyEmail: String(customer?.email || "").trim(),
    partyPhone: String(customer?.phone || "").trim(),
  });
  const selectCustomer = (customer) => {
    setForm((current) => ({
      ...current,
      ...(customer
        ? customerSnapshot(customer)
        : {
            partyId: "",
            partyType: "customer",
            partyName: "",
            partyDocument: "",
            partyEmail: "",
            partyPhone: "",
          }),
    }));
  };
  const updateItem = React.useCallback((index, patch) => setForm((current) => ({
    ...current,
    items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)),
  })), []);
  const addItem = () => setForm((current) => ({ ...current, items: [...current.items, emptyItem()] }));
  const removeItem = (index) => setForm((current) => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }));
  const stepAdjustmentDelta = React.useCallback((index, step) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        const currentDelta = Number(item.adjustmentDelta);
        const nextDelta = (Number.isFinite(currentDelta) ? currentDelta : 0) + step;
        return { ...item, adjustmentDelta: String(nextDelta) };
      }),
    }));
  }, []);
  const addPayment = () => setForm((current) => ({
    ...current,
    payments: [...current.payments, { ...emptyPayment(), amount: String(Math.max(0, pending)) }],
  }));
  const updatePayment = (index, patch) => setForm((current) => ({
    ...current,
    payments: current.payments.map((payment, paymentIndex) => (paymentIndex === index ? { ...payment, ...patch } : payment)),
  }));
  const fetchMercadoPagoSettlement = async (index) => {
    const payment = form.payments[index];
    const paymentId = String(payment?.providerPaymentId || payment?.reference || "").trim();
    if (!paymentId) {
      setStepError("Pegá el ID del pago de Mercado Pago para traer la liquidación real.");
      return;
    }
    try {
      setSettlementLoadingIndex(index);
      setStepError("");
      const data = await erpApi.getMercadoPagoSettlement(projectId, paymentId);
      const settlement = data?.settlement || {};
      updatePayment(index, {
        providerPaymentId: settlement.providerPaymentId || paymentId,
        installments: String(settlement.installments || 1),
        commissionAmount: String(settlement.commissionAmount || 0),
        commissionTaxAmount: String(settlement.commissionTaxAmount || 0),
        financingCostAmount: String(settlement.financingCostAmount || 0),
        withholdingAmount: String(settlement.withholdingAmount || 0),
        settlementSource: "provider",
        settlementStatus: "confirmed",
        settlementNetAmount: String(settlement.netAmount || 0),
        providerGrossAmount: String(settlement.grossAmount || 0),
      });
    } catch (err) {
      setStepError(err.message || "No se pudo consultar la liquidación de Mercado Pago.");
    } finally {
      setSettlementLoadingIndex(-1);
    }
  };
  const removePayment = (index) => setForm((current) => ({
    ...current,
    payments: current.payments.filter((_, paymentIndex) => paymentIndex !== index),
  }));

  const buildProductSelectionPatch = React.useCallback((product, preferredVariantId = "", kind = "sale") => {
    const capabilities = productCapabilities(product);
    const storedCost = product?.costing?.lastCost ?? product?.costing?.averageCost ?? product?.cost ?? 0;
    const usesCost = ["purchase", "expense", "opening_balance"].includes(kind);
    const nonMonetaryStockMove = ["stock_in", "stock_out", "stock_adjustment"].includes(kind);
    const variants = productVariants(product);
    const preferredVariant = preferredVariantId
      ? variants.find((variant) => variantIdOf(variant) === String(preferredVariantId))
      : null;
    const automaticVariant = preferredVariant || (variants.length === 1 ? variants[0] : null);
    const distributesVariants = VARIANT_DISTRIBUTION_KINDS.has(kind)
      && capabilities.stockTracked === true
      && variants.length > 1;
    const variantQuantities = distributesVariants
      ? Object.fromEntries(variants.map((variant) => [
          variantIdOf(variant),
          preferredVariant && variantIdOf(variant) === variantIdOf(preferredVariant) ? "1" : "",
        ]))
      : {};
    return {
      productId: entityId(product),
      variantId: distributesVariants ? "" : (automaticVariant ? variantIdOf(automaticVariant) : ""),
      name: projectProductName(product),
      code: projectProductCode(product),
      itemType: capabilities.kind,
      unitPrice: nonMonetaryStockMove
        ? "0"
        : usesCost
          ? String(storedCost || projectProductPrice(product))
          : String(automaticVariant ? variantPriceOf(product, automaticVariant) : projectProductPrice(product)),
      unitCost: String(storedCost || 0),
      tracksStock: capabilities.stockTracked === true,
      stockReceiptPending: false,
      stockIdentificationPending: false,
      variantQuantities,
      variantReceiptPending: distributesVariants
        ? Object.fromEntries(variants.map((variant) => [variantIdOf(variant), false]))
        : {},
      accountingAccountCode: kind === "sale"
        ? product?.accounting?.salesAccountCode || ""
        : kind === "purchase"
          ? capabilities.stockTracked === true
            ? product?.accounting?.inventoryAccountCode || ""
            : product?.accounting?.purchaseAccountCode || ""
          : kind === "expense"
            ? product?.accounting?.expenseAccountCode || product?.accounting?.purchaseAccountCode || ""
            : kind === "opening_balance"
              ? product?.accounting?.inventoryAccountCode || ""
              : "",
    };
  }, [productCapabilities]);

  const applyProductSelection = React.useCallback((index, product, preferredVariantId = "") => {
    if (!product) {
      updateItem(index, {
        productId: "",
        variantId: "",
        name: "",
        code: "",
        tracksStock: false,
        stockReceiptPending: false,
        stockIdentificationPending: false,
        variantQuantities: {},
        variantReceiptPending: {},
      });
      return;
    }
    updateItem(index, buildProductSelectionPatch(product, preferredVariantId, form.kind));
  }, [buildProductSelectionPatch, form.kind, updateItem]);

  const selectProduct = React.useCallback((index, product) => {
    if (product) rememberProducts(product);
    applyProductSelection(index, product || null);
  }, [applyProductSelection, rememberProducts]);

  const setIdentificationPending = React.useCallback((index, enabled) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => itemIndex === index
        ? enabled
          ? {
              ...item,
              productId: "",
              variantId: "",
              tracksStock: true,
              stockReceiptPending: false,
              stockIdentificationPending: true,
              variantQuantities: {},
              variantReceiptPending: {},
              accountingAccountCode: "",
            }
          : {
              ...item,
              tracksStock: false,
              stockIdentificationPending: false,
            }
        : item),
    }));
  }, []);

  const scanProductIntoLine = React.useCallback(async (rawCode) => {
    const index = scannerItemIndex;
    if (!Number.isInteger(index)) throw new Error("Elegí una línea de movimiento para escanear.");
    const code = String(rawCode || "").trim();
    const data = await productsApi.lookupCode(projectId, code);
    const product = data?.product;
    if (!product) throw new Error(`No se encontró ningún ítem con el código ${code}.`);
    if (!productSelectableForCurrentKind(product)) {
      throw new Error("El ítem existe, pero no está habilitado para este tipo de movimiento.");
    }

    const matchedVariantId = String(data?.matchedVariantId || "");
    if (matchedVariantId && !productVariants(product).some((variant) => variantIdOf(variant) === matchedVariantId)) {
      throw new Error("El código pertenece a una variante inactiva. Activala antes de usarla en movimientos.");
    }

    rememberProducts(product);
    applyProductSelection(index, product, matchedVariantId);
    setError("");
  }, [applyProductSelection, productSelectableForCurrentKind, projectId, rememberProducts, scannerItemIndex]);

  React.useEffect(() => {
    if (!open || movement || !initialProductId) return;
    const applyKey = `${projectId}:${initialProductId}`;
    if (initialProductAppliedRef.current === applyKey) return;
    const product = entityId(initialProduct) === initialProductId
      ? initialProduct
      : products.find((candidate) => entityId(candidate) === initialProductId);
    if (!product) return;
    rememberProducts(product);
    initialProductAppliedRef.current = applyKey;
    setForm((current) => patchFormItem(
      current,
      0,
      buildProductSelectionPatch(product, "", current.kind),
    ));
    if (baselineFormRef.current) {
      baselineFormRef.current = patchFormItem(
        baselineFormRef.current,
        0,
        buildProductSelectionPatch(product, "", baselineFormRef.current.kind),
      );
    }
  }, [buildProductSelectionPatch, initialProduct, initialProductId, movement, open, products, projectId, rememberProducts]);

  const selectVariant = (index, variantId) => {
    const item = form.items[index];
    const product = products.find((candidate) => entityId(candidate) === item?.productId);
    const variant = productVariants(product).find((candidate) => variantIdOf(candidate) === variantId);
    const usesCost = ["purchase", "expense", "opening_balance"].includes(form.kind);
    const nonMonetaryStockMove = ["stock_in", "stock_out", "stock_adjustment"].includes(form.kind);
    updateItem(index, {
      variantId,
      ...(!usesCost && !nonMonetaryStockMove && variant ? { unitPrice: String(variantPriceOf(product, variant)) } : {}),
    });
  };

  const usesVariantDistribution = React.useCallback((item, variants) => (
    VARIANT_DISTRIBUTION_KINDS.has(form.kind)
    && item?.tracksStock === true
    && Array.isArray(variants)
    && variants.length > 1
  ), [form.kind]);

  const updateVariantQuantity = React.useCallback((index, variantId, value) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => itemIndex === index
        ? {
            ...item,
            variantQuantities: {
              ...(item.variantQuantities || {}),
              [variantId]: value,
            },
          }
        : item),
    }));
  }, []);

  const updateVariantReceiptPending = React.useCallback((index, variantId, pending) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => itemIndex === index
        ? {
            ...item,
            variantReceiptPending: {
              ...(item.variantReceiptPending || {}),
              [variantId]: pending === true,
            },
          }
        : item),
    }));
  }, []);

  const variantsForItem = (item) => {
    const product = products.find((candidate) => entityId(candidate) === item?.productId);
    return { product, variants: productVariants(product) };
  };

  const chooseKind = (kind) => {
    if (catalogStockFlow) setCatalogOriginChosen(true);
    if (!movement && form.kind !== kind) setMaxVisitedStep(0);
    setStepError("");
    setForm((current) => {
      const oldAutoLabel = KIND_OPTIONS.find((option) => option.value === current.kind)?.label || "";
      const newAutoLabel = KIND_OPTIONS.find((option) => option.value === kind)?.label || "";
      const currentDescription = String(current.description || "").trim();
      const normalizedOldAutoLabel = oldAutoLabel.trim();
      const catalogAutoDescriptions = [
        "Compra de mercadería desde catálogo",
        "Stock inicial desde catálogo",
        "Ingreso de stock desde catálogo",
        "Ajuste de stock desde catálogo",
      ];
      const catalogDescription = kind === "purchase"
        ? "Compra de mercadería desde catálogo"
        : kind === "opening_balance"
          ? "Stock inicial desde catálogo"
          : kind === "stock_adjustment"
            ? "Ajuste de stock desde catálogo"
            : "Ingreso de stock desde catálogo";
      const shouldReplaceDescription = !currentDescription
        || currentDescription === normalizedOldAutoLabel
        || catalogAutoDescriptions.includes(currentDescription);

      return {
        ...current,
        kind,
        partyType: ["purchase", "expense", "payment"].includes(kind) ? "supplier" : "customer",
        partyId: ["sale", "collection"].includes(kind) ? current.partyId : "",
        payments: COMMERCIAL_KINDS.has(kind) ? current.payments : [],
        items: ITEM_KINDS.has(kind)
          ? (current.items.length ? current.items : [emptyItem()]).map((item) => {
              const product = products.find((candidate) => entityId(candidate) === item.productId);
              const variants = productVariants(product);
              const shouldDistribute = VARIANT_DISTRIBUTION_KINDS.has(kind)
                && item.tracksStock === true
                && variants.length > 1;
              if (shouldDistribute) {
                const existingQuantities = item.variantQuantities && Object.keys(item.variantQuantities).length
                  ? item.variantQuantities
                  : Object.fromEntries(variants.map((variant) => [
                      variantIdOf(variant),
                      item.variantId === variantIdOf(variant) ? item.quantity : "",
                    ]));
                return {
                  ...item,
                  stockIdentificationPending: kind === "purchase" && item.stockIdentificationPending === true,
                  variantId: "",
                  variantQuantities: existingQuantities,
                  variantReceiptPending: Object.fromEntries(variants.map((variant) => [
                    variantIdOf(variant),
                    item.variantReceiptPending?.[variantIdOf(variant)] === true || (
                      item.variantId === variantIdOf(variant) && item.stockReceiptPending === true
                    ),
                  ])),
                  accountingAccountCode: "",
                };
              }
              return {
                ...item,
                stockIdentificationPending: kind === "purchase" && item.stockIdentificationPending === true,
                tracksStock: kind === "purchase" && item.stockIdentificationPending === true
                  ? true
                  : item.tracksStock,
                quantity: String(distributedQuantity(item) || item.quantity || "1"),
                variantQuantities: {},
                variantReceiptPending: {},
                accountingAccountCode: "",
              };
            })
          : current.items,
        description: shouldReplaceDescription
          ? (catalogStockFlow ? catalogDescription : newAutoLabel)
          : current.description,
      };
    });
  };

  const registerOptionLabel = (register) => {
    const kindLabel = CASH_REGISTER_KIND_LABELS[register.kind] || "Caja o cuenta";
    const sessionText = cashRegisterNeedsSession(register)
      ? (openRegisterIds.has(entityId(register)) ? " · abierta" : " · cerrada")
      : "";
    return `${register.name} · ${kindLabel}${sessionText}`;
  };

  const buildPayload = (partyOverride = null) => {
    const serializeItem = (item, { quantity, variantId, stockReceiptPending, discountAmount, taxAmount } = {}) => ({
      productId: item.productId || undefined,
      variantId: variantId || item.variantId || undefined,
      name: item.name,
      code: item.code || undefined,
      itemType: item.itemType,
      quantity: stockAdjustment
        ? Math.abs(safeNumber(item.adjustmentDelta))
        : safeNumber(quantity ?? item.quantity),
      stockDelta: stockAdjustment ? safeNumber(item.adjustmentDelta) : undefined,
      unitPrice: (stockAdjustment || manualStockMove)
        ? 0
        : ["purchase", "expense", "opening_balance"].includes(form.kind)
          ? safeNumber(item.unitCost || item.unitPrice)
          : safeNumber(item.unitPrice),
      unitCost: (stockAdjustment || manualStockMove) ? undefined : safeNumber(item.unitCost),
      discountAmount: (stockAdjustment || manualStockMove) ? 0 : safeNumber(discountAmount ?? item.discountAmount),
      taxAmount: (stockAdjustment || manualStockMove) ? 0 : safeNumber(taxAmount ?? item.taxAmount),
      tracksStock: Boolean(item.tracksStock),
      ...(form.kind === "purchase" && item.stockIdentificationPending
        ? {
            stockIdentificationPending: true,
            stockReceiptStatus: "unidentified",
          }
        : {}),
      ...(form.kind === "purchase" && item.tracksStock
        && !item.stockIdentificationPending
        ? (() => {
            const pendingReceipt = stockReceiptPending ?? (item.stockReceiptPending === true);
            return {
              stockReceiptPending: pendingReceipt,
              stockReceiptStatus: pendingReceipt ? "pending" : "received",
            };
          })()
        : {}),
      ...(canChooseAccountingAccount && item.accountingAccountCode
        ? form.kind === "sale"
          ? { salesAccountCodeSnapshot: item.accountingAccountCode }
          : form.kind === "purchase"
            ? item.tracksStock
              ? { inventoryAccountCodeSnapshot: item.accountingAccountCode }
              : { purchaseAccountCodeSnapshot: item.accountingAccountCode }
            : form.kind === "expense"
              ? { expenseAccountCodeSnapshot: item.accountingAccountCode }
              : form.kind === "opening_balance"
                ? { inventoryAccountCodeSnapshot: item.accountingAccountCode }
                : {}
        : {}),
    });

    const items = usesItems
      ? form.items.flatMap((item) => {
          const { variants } = variantsForItem(item);
          if (!usesVariantDistribution(item, variants)) return [serializeItem(item)];

          const allocations = variants
            .map((variant) => ({
              variantId: variantIdOf(variant),
              quantity: Math.max(0, safeNumber(item.variantQuantities?.[variantIdOf(variant)])),
            }))
            .filter((allocation) => allocation.quantity > 0);
          const allocationTotal = allocations.reduce((sum, allocation) => sum + allocation.quantity, 0);
          const totalDiscount = safeNumber(item.discountAmount);
          const totalTax = safeNumber(item.taxAmount);

          return allocations.map((allocation, allocationIndex) => {
            const isLast = allocationIndex === allocations.length - 1;
            const precedingQuantity = allocations
              .slice(0, allocationIndex)
              .reduce((sum, current) => sum + current.quantity, 0);
            const precedingDiscount = allocationIndex
              ? totalDiscount * (precedingQuantity / allocationTotal)
              : 0;
            const precedingTax = allocationIndex
              ? totalTax * (precedingQuantity / allocationTotal)
              : 0;
            const proportionalDiscount = totalDiscount * (allocation.quantity / allocationTotal);
            const proportionalTax = totalTax * (allocation.quantity / allocationTotal);
            return serializeItem(item, {
              quantity: allocation.quantity,
              variantId: allocation.variantId,
              stockReceiptPending: item.variantReceiptPending?.[allocation.variantId] === true,
              discountAmount: isLast
                ? totalDiscount - precedingDiscount
                : proportionalDiscount,
              taxAmount: isLast
                ? totalTax - precedingTax
                : proportionalTax,
            });
          });
        })
      : [];

    let payments = [];
    if (commercial) {
      payments = form.payments
        .filter((payment) => safeNumber(payment.amount) > 0)
        .map((payment) => {
          const register = registerById(payment.cashRegisterId);
          const settlement = paymentSettlementPreview(register, payment);
          const hasManualOverride = [
            payment.commissionAmount,
            payment.commissionTaxAmount,
            payment.financingCostAmount,
            payment.withholdingAmount,
          ].some((value) => value !== "" && value !== undefined);
          return {
            method: paymentMethodForCashRegister(register),
            amount: safeNumber(payment.amount),
            cashRegisterId: payment.cashRegisterId,
            reference: payment.reference,
            settlement: {
              provider: settlement.provider,
              source: payment.settlementSource
                || (settlement.provider === "mercadopago" ? "register_default" : hasManualOverride ? "manual_override" : "register_default"),
              status: payment.settlementStatus
                || (settlement.provider === "mercadopago" ? "pending_provider" : settlement.provider === "manual" ? "estimated" : "not_applicable"),
              installments: settlement.installments,
              commissionAmount: settlement.commissionAmount,
              commissionTaxAmount: settlement.commissionTaxAmount,
              financingCostAmount: settlement.financingCostAmount,
              withholdingAmount: settlement.withholdingAmount,
              totalCost: settlement.totalCost,
              netAmount: settlement.netAmount,
              providerGrossAmount: safeNumber(payment.providerGrossAmount),
              providerPaymentId: payment.providerPaymentId || payment.reference || "",
            },
          };
        });
    } else if (manualMoney) {
      const register = registerById(form.moneyRegisterId);
      payments = [{
        method: paymentMethodForCashRegister(register),
        amount: total,
        cashRegisterId: form.moneyRegisterId,
        reference: form.paymentReference,
      }];
    }

    const selectedRegister = transferMode
      ? registerById(form.transferFromId)
      : manualMoney
        ? registerById(form.moneyRegisterId)
        : registerById(form.payments.find((payment) => safeNumber(payment.amount) > 0)?.cashRegisterId);

    return {
      kind: form.kind,
      currency: selectedRegister?.currency || "ARS",
      occurredAt: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
      documentDate: form.occurredAt ? new Date(form.occurredAt).toISOString() : undefined,
      description: form.description,
      notes: form.notes,
      sourceType: openingInventory ? "inventory_onboarding" : "manual_panel",
      sourceNumber: form.sourceNumber,
      idempotencyKey: form.idempotencyKey,
      total: usesItems ? undefined : total,
      party: {
        partyId: requiresParty ? (partyOverride?.partyId || form.partyId || undefined) : undefined,
        type: requiresParty ? (partyOverride?.partyType || form.partyType) : "other",
        name: requiresParty ? (partyOverride?.partyName ?? form.partyName) : "",
        document: requiresParty ? (partyOverride?.partyDocument ?? form.partyDocument) : "",
        email: requiresParty ? (partyOverride?.partyEmail ?? form.partyEmail) : "",
        phone: requiresParty ? (partyOverride?.partyPhone ?? form.partyPhone) : "",
      },
      items,
      payments,
      ...(openingInventory ? { metadata: { counterAccountCode: "OWNER_EQUITY", openingInventory: true } } : {}),
      ...(transferMode ? {
        transfer: {
          fromCashRegisterId: form.transferFromId,
          toCashRegisterId: form.transferToId,
          amount: total,
        },
      } : {}),
    };
  };

  const validateRegister = (registerId, directionLabel) => {
    const register = registerById(registerId);
    if (!register) return `Elegí ${directionLabel}.`;
    if (!registerIsUsable(register)) return `${register.name} está cerrada. Abrila antes de usar efectivo.`;
    return "";
  };

  const validateItemIdentityStep = () => {
    if (!usesItems) return "";
    if (!form.items.length) return "Agregá al menos un ítem.";
    for (let index = 0; index < form.items.length; index += 1) {
      const item = form.items[index];
      if (!item.name.trim()) return `Completá el ítem ${index + 1}.`;
      if (requiresStockProduct && !item.productId) return `Elegí un producto para la línea ${index + 1}.`;
      const { variants } = variantsForItem(item);
      const distributed = usesVariantDistribution(item, variants);
      if (!item.stockIdentificationPending && item.productId && variants.length && !distributed && !item.variantId) {
        return `Elegí la variante de la línea ${index + 1}.`;
      }
    }
    return "";
  };

  const validateItemQuantityStep = () => {
    if (!usesItems) return "";
    for (let index = 0; index < form.items.length; index += 1) {
      const item = form.items[index];
      const { variants } = variantsForItem(item);
      const distributed = usesVariantDistribution(item, variants);
      if (distributed && distributedQuantity(item) <= 0) return `Cargá al menos una cantidad por variante en la línea ${index + 1}.`;
      if (stockAdjustment && safeNumber(item.adjustmentDelta) === 0) return `La corrección de la línea ${index + 1} no puede ser cero.`;
      if (!stockAdjustment && !distributed && safeNumber(item.quantity) <= 0) return `La cantidad de la línea ${index + 1} debe ser mayor a cero.`;
    }
    return "";
  };

  const validateItemValueStep = () => {
    if (!hasItemValueStep) return "";
    for (let index = 0; index < form.items.length; index += 1) {
      const item = form.items[index];
      const unitValue = ["purchase", "expense", "opening_balance"].includes(form.kind)
        ? safeNumber(item.unitCost || item.unitPrice)
        : safeNumber(item.unitPrice);
      if (unitValue <= 0) return `Completá el ${form.kind === "sale" ? "precio" : "costo"} del ítem ${index + 1}.`;
    }
    return "";
  };

  const validateMoneyStep = () => {
    if ((manualMoney || transferMode) && total <= 0) return "Indicá un importe mayor a cero.";
    if (commercial && paid > total) return "El dinero registrado no puede superar el total.";
    if (commercial) {
      const paymentCurrencies = new Set(
        form.payments
          .filter((payment) => safeNumber(payment.amount) > 0)
          .map((payment) => registerById(payment.cashRegisterId)?.currency)
          .filter(Boolean),
      );
      if (paymentCurrencies.size > 1) return "Para cobrar o pagar en monedas distintas, registrá cada moneda como una operación separada.";
    }
    for (const payment of form.payments) {
      if (safeNumber(payment.amount) <= 0) continue;
      const registerError = validateRegister(payment.cashRegisterId, "dónde entró o salió el dinero");
      if (registerError) return registerError;
    }
    if (manualMoney) {
      return validateRegister(
        form.moneyRegisterId,
        ["collection", "cash_in"].includes(form.kind) ? "dónde ingresó el dinero" : "de dónde salió el dinero",
      );
    }
    if (transferMode) {
      const fromError = validateRegister(form.transferFromId, "la caja o cuenta de origen");
      if (fromError) return fromError;
      const toError = validateRegister(form.transferToId, "la caja o cuenta de destino");
      if (toError) return toError;
      if (form.transferFromId === form.transferToId) return "El origen y el destino deben ser distintos.";
      const from = registerById(form.transferFromId);
      const to = registerById(form.transferToId);
      if (from?.currency !== to?.currency) return "Las transferencias entre monedas distintas se registran como una salida y un ingreso.";
    }
    return "";
  };

  const validateStepperStep = (stepId) => {
    if (stepId === "origin") {
      if (!catalogOriginChosen) return "Elegí de dónde viene este stock. Eso define qué va a registrar WebProfit.";
      return "";
    }
    if (stepId === "kind") {
      return allowedKinds.some((option) => option.value === form.kind) ? "" : "Elegí qué pasó en tu negocio.";
    }
    if (stepId === "description") return form.description.trim() ? "" : "Contá brevemente qué pasó.";
    if (stepId === "item") return validateItemIdentityStep();
    if (stepId === "quantity") {
      const identityError = validateItemIdentityStep();
      return identityError || validateItemQuantityStep();
    }
    if (stepId === "value") return validateItemValueStep();
    if (stepId === "money") return validateMoneyStep();
    if (stepId === "review") return validate();
    return "";
  };

  const validate = () => {
    if (!form.description.trim()) return "Contá brevemente qué pasó.";
    if (!allowedKinds.some((option) => option.value === form.kind)) return "No tenés permiso para registrar esta acción.";

    if (usesItems) {
      if (!form.items.length) return "Agregá al menos un ítem.";
      for (let index = 0; index < form.items.length; index += 1) {
        const item = form.items[index];
        if (!item.name.trim()) return `Completá el ítem ${index + 1}.`;
        if (requiresStockProduct && !item.productId) return `Elegí un producto para la línea ${index + 1}.`;
        const { variants } = variantsForItem(item);
        const distributed = usesVariantDistribution(item, variants);
        if (!item.stockIdentificationPending && item.productId && variants.length && !distributed && !item.variantId) return `Elegí la variante de la línea ${index + 1}.`;
        if (distributed && distributedQuantity(item) <= 0) return `Cargá al menos una cantidad por variante en la línea ${index + 1}.`;
        if (stockAdjustment && safeNumber(item.adjustmentDelta) === 0) return `La corrección de la línea ${index + 1} no puede ser cero.`;
        if (!stockAdjustment && !distributed && safeNumber(item.quantity) <= 0) return `La cantidad de la línea ${index + 1} debe ser mayor a cero.`;
        if (["sale", "purchase", "expense", "opening_balance"].includes(form.kind)) {
          const unitValue = ["purchase", "expense", "opening_balance"].includes(form.kind)
            ? safeNumber(item.unitCost || item.unitPrice)
            : safeNumber(item.unitPrice);
          if (unitValue <= 0) return `Completá el importe del ítem ${index + 1}.`;
        }
      }
    }

    if ((manualMoney || transferMode) && total <= 0) return "Indicá un importe mayor a cero.";
    if (commercial && paid > total) return "El dinero registrado no puede superar el total.";
    if (commercial) {
      const paymentCurrencies = new Set(
        form.payments
          .filter((payment) => safeNumber(payment.amount) > 0)
          .map((payment) => registerById(payment.cashRegisterId)?.currency)
          .filter(Boolean),
      );
      if (paymentCurrencies.size > 1) {
        return "Para cobrar o pagar en monedas distintas, registrá cada moneda como una operación separada.";
      }
    }

    for (const payment of form.payments) {
      if (safeNumber(payment.amount) <= 0) continue;
      const registerError = validateRegister(payment.cashRegisterId, "dónde entró o salió el dinero");
      if (registerError) return registerError;
    }
    if (manualMoney) {
      const registerError = validateRegister(
        form.moneyRegisterId,
        ["collection", "cash_in"].includes(form.kind) ? "dónde ingresó el dinero" : "de dónde salió el dinero",
      );
      if (registerError) return registerError;
    }
    if (transferMode) {
      const fromError = validateRegister(form.transferFromId, "la caja o cuenta de origen");
      if (fromError) return fromError;
      const toError = validateRegister(form.transferToId, "la caja o cuenta de destino");
      if (toError) return toError;
      if (form.transferFromId === form.transferToId) return "El origen y el destino deben ser distintos.";
      const from = registerById(form.transferFromId);
      const to = registerById(form.transferToId);
      if (from?.currency !== to?.currency) return "Las transferencias entre monedas distintas se registran como una salida y un ingreso.";
    }
    return "";
  };

  const ensureCrmCustomer = async () => {
    if (!customerMovement || form.partyId || !canManageCustomers) return null;
    const name = String(form.partyName || "").trim();
    const email = String(form.partyEmail || "").trim();
    const phone = String(form.partyPhone || "").trim();
    const document = String(form.partyDocument || "").trim();
    if (!name || (!email && !phone && !document)) return null;

    try {
      const data = await customersApi.create(projectId, { name, email, phone, document, source: "erp_movement" });
      const customer = data?.customer || null;
      if (customer) {
        const snapshot = customerSnapshot(customer);
        setCustomers((current) => {
          const id = customerIdOf(customer);
          return current.some((item) => customerIdOf(item) === id) ? current : [customer, ...current];
        });
        setForm((current) => ({ ...current, ...snapshot }));
        return snapshot;
      }
      return null;
    } catch (requestError) {
      if (requestError?.code === "CUSTOMER_IDENTITY_EXISTS" && requestError?.data?.customerId && canReadCustomers) {
        const existing = await customersApi.get(projectId, requestError.data.customerId);
        const customer = existing?.customer || null;
        if (customer) {
          const snapshot = customerSnapshot(customer);
          setForm((current) => ({ ...current, ...snapshot }));
          return snapshot;
        }
      }
      if (requestError?.code === "CUSTOMER_POSSIBLE_DUPLICATE") {
        throw new Error("Hay una ficha posiblemente duplicada en el CRM. Seleccioná el cliente existente o revisá sus datos antes de guardar.");
      }
      throw requestError;
    }
  };

  const closeImmediately = React.useCallback(() => {
    setDiscardConfirmOpen(false);
    onClose?.();
  }, [onClose]);

  const requestClose = React.useCallback(() => {
    if (saving) return;
    const baseline = baselineFormRef.current;
    if (!baseline || formFingerprint(form) === formFingerprint(baseline)) {
      closeImmediately();
      return;
    }
    setDiscardConfirmOpen(true);
  }, [closeImmediately, form, saving]);

  const saveDraft = async ({ reviewImpact = false } = {}) => {
    const validationError = validate();
    if (validationError) {
      setError("");
      setSaveErrorDialogMessage(validationError);
      return;
    }
    try {
      setSaving(true);
      setError("");
      setSaveErrorDialogMessage("");
      const customerParty = await ensureCrmCustomer();
      const payload = buildPayload(customerParty);
      const movementId = entityId(draftMovement || movement);
      const response = movementId
        ? await erpApi.updateMovement(projectId, movementId, payload)
        : await erpApi.createMovementDraft(projectId, payload);
      const saved = response.movement;
      baselineFormRef.current = customerParty ? { ...form, ...customerParty } : form;
      setDraftMovement(saved);
      onSaved?.(saved);
      if (reviewImpact) {
        if (saved?.kind === "sale" || form.kind === "sale") {
          const party = saved?.party || {};
          const recipientName = String(party.name || customerParty?.partyName || form.partyName || "Consumidor final").trim();
          const recipientDocument = String(party.document || customerParty?.partyDocument || form.partyDocument || "").replace(/\D+/g, "").slice(0, 11);
          setFiscalAction("pending");
          setFiscalRecipient({
            name: recipientName || "Consumidor final",
            documentNumber: recipientDocument,
            taxConditionCode: 5,
            address: "",
          });
          setFiscalResult(null);
        }
        setConfirmationOpen(true);
      } else {
        closeImmediately();
      }
    } catch (err) {
      setSaveErrorDialogMessage(err.message || "No se pudo guardar el borrador");
    } finally {
      setSaving(false);
    }
  };

  const goToMovementStep = (nextStep) => {
    const clamped = Math.min(Math.max(0, nextStep), Math.max(0, movementSteps.length - 1));
    setActiveStep(clamped);
    setMaxVisitedStep((current) => Math.max(current, clamped));
    setStepError("");
    setError("");
  };

  const goToNextMovementStep = () => {
    const validationError = validateStepperStep(activeStepId);
    if (validationError) {
      setStepError(validationError);
      return;
    }
    setStepError("");
    if (safeActiveStep >= movementSteps.length - 1) {
      saveDraft({ reviewImpact: true });
      return;
    }
    goToMovementStep(safeActiveStep + 1);
  };

  const skipMovementStep = () => {
    if (!activeStepMeta?.optional || safeActiveStep >= movementSteps.length - 1) return;
    goToMovementStep(safeActiveStep + 1);
  };

  const confirmDraft = async () => {
    const pendingMercadoPago = form.payments.find((payment) => {
      if (safeNumber(payment.amount) <= 0) return false;
      const register = registerById(payment.cashRegisterId);
      return register?.paymentProcessing?.provider === "mercadopago"
        && (payment.settlementSource !== "provider" || safeNumber(payment.providerGrossAmount) <= 0);
    });
    if (["sale", "collection"].includes(form.kind) && pendingMercadoPago) {
      setConfirmationOpen(false);
      setError("Antes de confirmar, traé los costos reales del pago de Mercado Pago. Necesitamos el neto real que entró a la caja.");
      return;
    }
    const movementId = entityId(draftMovement);
    if (!movementId) {
      setConfirmationOpen(false);
      setError("Primero guardá el borrador para poder confirmarlo.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const response = await erpApi.confirmMovement(projectId, movementId);
      const confirmed = response.movement;
      setDraftMovement(confirmed);
      onSaved?.(confirmed);

      if (form.kind === "sale" && fiscalAction === "issue") {
        try {
          const created = await fiscalApi.createFromMovement(projectId, movementId, {
            name: fiscalRecipient.name || "Consumidor final",
            documentNumber: fiscalRecipient.documentNumber,
            taxConditionCode: Number(fiscalRecipient.taxConditionCode || 5),
            address: fiscalRecipient.address,
          });
          const fiscalDocument = created?.document;
          const issued = fiscalDocument?._id || fiscalDocument?.id
            ? await fiscalApi.issue(projectId, fiscalDocument._id || fiscalDocument.id)
            : created;
          const finalDocument = issued?.document || fiscalDocument || {};
          setFiscalResult({
            status: finalDocument.status === "authorized" ? "authorized" : "pending",
            displayNumber: finalDocument.displayNumber || "",
            cae: finalDocument.cae || "",
            message: finalDocument.status === "authorized"
              ? "La venta quedó confirmada y ARCA autorizó el comprobante."
              : "La venta quedó confirmada y el comprobante quedó en conciliación segura con ARCA.",
          });
        } catch (fiscalError) {
          setFiscalResult({
            status: "error",
            displayNumber: "",
            cae: "",
            message: `La venta quedó confirmada correctamente, pero la factura no pudo emitirse ahora. ${fiscalError?.message || "Quedó disponible para resolverla desde Facturación."}`,
          });
        }
        setConfirmationOpen(false);
        return;
      }

      setConfirmationOpen(false);
      closeImmediately();
    } catch (err) {
      setError(err.message || "No se pudo confirmar el movimiento");
      setConfirmationOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const hasEnteredPayment = form.payments.some((payment) => safeNumber(payment.amount) > 0);
  const showMoneySetupWarning = !registers.length && (manualMoney || transferMode || (commercial && hasEnteredPayment));
  const selectedKind = KIND_OPTIONS.find((option) => option.value === form.kind);
  const catalogQuantityPreview = form.items.reduce((sum, item) => {
    if (stockAdjustment) return sum + safeNumber(item.adjustmentDelta);
    const { variants } = variantsForItem(item);
    return sum + (usesVariantDistribution(item, variants) ? distributedQuantity(item) : safeNumber(item.quantity));
  }, 0);
  const canConfirm = hasProjectPermission(access, "erp.movements.confirm");
  const confirmationImpact = form.kind === "purchase"
    ? "Al confirmar, la compra registra importes y pagos. Las líneas en tránsito o sin variante identificada no entran al stock físico hasta su recepción."
    : form.kind === "sale"
      ? "Al confirmar, la venta descuenta stock y registra el ingreso o el saldo pendiente."
      : openingInventory
        ? "Al confirmar, se carga el inventario inicial sin sacar dinero de ninguna caja."
        : stockIn
          ? "Al confirmar, se sumarán las unidades indicadas sin crear una compra ni mover dinero."
          : stockOut
            ? "Al confirmar, se descontarán las unidades indicadas sin crear una venta ni mover dinero."
            : stockAdjustment
              ? "Al confirmar, se corrige la existencia y queda trazabilidad de la diferencia."
              : "El impacto en dinero y registros internos ocurre recién cuando confirmás.";
  const reviewMovement = draftMovement || {};
  const reviewCurrency = reviewMovement.currency || "ARS";
  const reviewTotal = safeNumber(reviewMovement.total);
  const reviewPaid = safeNumber(reviewMovement.paidAmount);
  const reviewPending = safeNumber(reviewMovement.balanceDue);
  const reviewStockIn = (reviewMovement.items || []).reduce(
    (sum, item) => sum + (["pending", "unidentified"].includes(item.stockReceiptStatus) ? 0 : Math.max(0, safeNumber(item.stockDelta))),
    0,
  );
  const reviewPendingStock = reviewMovement.kind === "purchase"
    ? (reviewMovement.items || []).reduce(
        (sum, item) => sum + (["pending", "unidentified"].includes(item.stockReceiptStatus) ? Math.max(0, safeNumber(item.stockDelta)) : 0),
        0,
      )
    : 0;
  const reviewStockOut = (reviewMovement.items || []).reduce(
    (sum, item) => sum + Math.max(0, -safeNumber(item.stockDelta)),
    0,
  );
  const reviewFacts = [];
  if (reviewStockIn > 0) {
    reviewFacts.push(`Stock: se sumarán ${reviewStockIn} unidad/es.`);
  }
  if (reviewPendingStock > 0) {
    reviewFacts.push(`Pendiente de recepción: ${reviewPendingStock} unidad/es no estarán disponibles para venta hasta que lleguen y, si hace falta, se identifiquen.`);
  }
  if (reviewStockOut > 0) {
    reviewFacts.push(`Stock: se descontarán ${reviewStockOut} unidad/es.`);
  }
  if (reviewPaid > 0 && reviewMovement.kind !== "transfer") {
    const moneyDirection = ["sale", "collection", "cash_in"].includes(reviewMovement.kind)
      ? "ingresarán"
      : ["purchase", "expense", "payment", "cash_out"].includes(reviewMovement.kind)
        ? "saldrán"
        : "se moverán";
    reviewFacts.push(`Dinero: ${moneyDirection} ${formatMoney(reviewPaid, reviewCurrency)} por los medios cargados.`);
  }
  if (reviewPending > 0) {
    const pendingLabel = reviewMovement.kind === "sale" ? "cuentas por cobrar" : "cuentas por pagar";
    reviewFacts.push(`Pendiente: ${formatMoney(reviewPending, reviewCurrency)} quedarán en ${pendingLabel}.`);
  }
  if (reviewMovement.kind === "transfer" && reviewTotal > 0) {
    const fromRegister = registerById(entityId(reviewMovement.transfer?.fromCashRegisterId));
    const toRegister = registerById(entityId(reviewMovement.transfer?.toCashRegisterId));
    reviewFacts.push(
      `Transferencia: ${formatMoney(reviewTotal, reviewCurrency)} pasarán de ${fromRegister?.name || "la cuenta de origen"} a ${toRegister?.name || "la cuenta de destino"}.`,
    );
  }
  if (reviewTotal > 0 || reviewStockIn > 0 || reviewPendingStock > 0 || reviewStockOut > 0) {
    reviewFacts.push("Contabilidad y trazabilidad: se generarán los registros internos correspondientes.");
  }
  if (!reviewFacts.length) {
    reviewFacts.push("Se guardará la trazabilidad del movimiento sin aplicar cambios monetarios ni de stock.");
  }
  const sectionSx = {
    p: catalogStockFlow ? { xs: 1.25, sm: 1.55 } : { xs: 1.5, sm: 2.2 },
    borderRadius: { xs: 2.5, sm: 3 },
    border: `1px solid ${ERP_BRAND.border}`,
    borderTop: `3px solid ${ERP_BRAND.orange}`,
    bgcolor: "var(--wp-panel-surface, #fff)",
    boxShadow: "0 12px 34px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.055)",
  };

  return (
    <>
    <Dialog
      open={open && !confirmationOpen}
      onClose={saving ? undefined : requestClose}
      fullWidth
      maxWidth={catalogStockFlow ? "md" : "lg"}
      fullScreen={mobile}
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          minHeight: mobile ? "100dvh" : "min(620px, calc(100dvh - 48px))",
          maxHeight: mobile ? "100dvh" : "calc(100dvh - 48px)",
          borderRadius: mobile ? 0 : 4,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          px: { xs: 2, sm: 3 },
          py: { xs: 1.4, sm: 1.7 },
          pr: { xs: 6, sm: 7 },
          color: "#fff",
          background: "linear-gradient(135deg,#19242F 0%,#2E4658 72%,#3C5A70 100%)",
          overflow: "hidden",
          "&::after": {
            content: '\"\"',
            position: "absolute",
            width: 150,
            height: 150,
            right: -55,
            bottom: -95,
            borderRadius: "50%",
            bgcolor: "rgba(243,90,58,.26)",
          },
        }}
      >
        <Typography variant="overline" sx={{ fontWeight: 950, letterSpacing: ".12em", color: "#FFAA96" }}>
          {catalogStockFlow ? "STOCK DEL ÍTEM" : "OPERACIÓN DEL NEGOCIO"}
        </Typography>
        <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950, lineHeight: 1.15 }}>
          {catalogStockFlow ? "¿Cómo llegó este stock?" : movement ? "Editar movimiento" : "Registrar movimiento"}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,.72)", mt: 0.45, maxWidth: 760 }}>
          {catalogStockFlow
            ? "El ítem ya está elegido. Primero contanos el origen; después WebProfit te muestra sólo lo necesario."
            : "Una decisión por vez. Lo importante queda adelante y lo opcional se puede omitir."}
        </Typography>
        <IconButton
          onClick={requestClose}
          disabled={saving}
          aria-label="Cerrar formulario"
          sx={{ position: "absolute", right: 10, top: 10, color: "#fff", zIndex: 1 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
          p: { xs: 1.15, sm: 1.6 },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack spacing={{ xs: 1.7, sm: 2.3 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 0.9, sm: 1 },
              borderRadius: 2.5,
              border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)",
              bgcolor: "var(--wp-panel-surface, #fff)",
            }}
          >
            {mobile ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={`${safeActiveStep + 1}/${movementSteps.length}`}
                  sx={{ fontWeight: 950, bgcolor: ERP_BRAND.orange, color: "#17181C" }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.1 }}>
                    {activeStepMeta?.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                    {activeStepMeta?.detail}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${movementSteps.length}, minmax(0, 1fr))`,
                  gap: 0.65,
                  "@keyframes wpMovementStepGlow": {
                    "0%,100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.10)" },
                    "50%": { boxShadow: "0 0 0 5px rgba(243,90,58,.08)" },
                  },
                  "@media (prefers-reduced-motion: reduce)": {
                    "& .wp-movement-step-active": { animation: "none" },
                  },
                }}
              >
                {movementSteps.map((step, index) => {
                  const active = index === safeActiveStep;
                  const visited = index <= stepAccessLimit;
                  const completed = visited && !active;
                  return (
                    <Box
                      component="button"
                      type="button"
                      key={step.id}
                      disabled={!visited}
                      onClick={() => visited && goToMovementStep(index)}
                      className={active ? "wp-movement-step-active" : undefined}
                      aria-current={active ? "step" : undefined}
                      title={visited ? `Ir a ${step.label}` : "Completá los pasos anteriores para llegar acá"}
                      sx={{
                        appearance: "none",
                        border: active
                          ? "1px solid rgba(243,90,58,.55)"
                          : completed
                            ? "1px solid rgba(46,125,50,.20)"
                            : "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)",
                        bgcolor: active
                          ? "rgba(243,90,58,.10)"
                          : completed ? "rgba(46,125,50,.05)" : "transparent",
                        borderRadius: 2,
                        minWidth: 0,
                        px: 0.65,
                        py: 0.58,
                        textAlign: "left",
                        cursor: visited ? "pointer" : "default",
                        opacity: visited ? 1 : 0.5,
                        transition: "transform .15s ease, border-color .15s ease, box-shadow .15s ease, background-color .15s ease",
                        animation: active ? "wpMovementStepGlow 2.1s ease-in-out infinite" : "none",
                        "&:hover": visited ? { transform: "translateY(-1px)", borderColor: "rgba(243,90,58,.32)" } : undefined,
                      }}
                    >
                      <Stack direction="row" spacing={0.55} alignItems="center">
                        <Box
                          sx={{
                            width: 21,
                            height: 21,
                            flex: "0 0 21px",
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            fontSize: ".65rem",
                            fontWeight: 950,
                            bgcolor: active ? ERP_BRAND.orange : completed ? "success.main" : "rgba(25,36,47,.10)",
                            color: active ? "#17181C" : completed ? "#fff" : ERP_BRAND.dark,
                          }}
                        >
                          {completed ? "✓" : index + 1}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: movementSteps.length > 9 ? ".66rem" : ".72rem",
                              fontWeight: 950,
                              color: ERP_BRAND.dark,
                              lineHeight: 1.08,
                              whiteSpace: "normal",
                            }}
                          >
                            {step.label}
                          </Typography>
                          {movementSteps.length <= 8 ? (
                            <Typography noWrap sx={{ fontSize: ".59rem", color: ERP_BRAND.muted, lineHeight: 1.1, mt: 0.16 }}>
                              {step.detail}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>

          {stepError ? <Alert severity="error" sx={{ py: 0.25 }}>{stepError}</Alert> : null}
          {error ? <Alert severity="error" sx={{ py: 0.25 }}>{error}</Alert> : null}
          {activeStepMeta?.optional && !isStep("review") ? (
            <Paper
              elevation={0}
              sx={{
                px: 1.2,
                py: 0.85,
                borderRadius: 2.2,
                border: "1px solid rgba(243,90,58,.20)",
                bgcolor: "rgba(243,90,58,.035)",
              }}
            >
              <Stack direction="row" spacing={0.9} alignItems="center">
                <Chip size="small" label="Opcional" sx={{ fontWeight: 950, bgcolor: "rgba(243,90,58,.12)", color: ERP_BRAND.dark }} />
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted, lineHeight: 1.3 }}>
                  Si esto no aplica ahora, omitilo. No se pierde lo que ya cargaste y podés volver desde los pasos de arriba.
                </Typography>
              </Stack>
            </Paper>
          ) : null}
          {showMoneySetupWarning && (isStep("money") || isStep("review")) ? (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" href={`/dashboard/proyectos/${projectId}/sistema/caja`}>
                  Crear cajas
                </Button>
              )}
            >
              Antes de mover dinero, creá las cajas y cuentas reales que usa tu negocio.
            </Alert>
          ) : null}

          {isStep("origin") && catalogStockFlow ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step="1"
                title="¿De dónde viene este stock?"
                description="Elegí una situación. WebProfit cambia el formulario para registrar sólo lo que realmente pasó."
              />
              <Grid container spacing={1}>
                {CATALOG_STOCK_KIND_OPTIONS
                  .filter((option) => allowedKinds.some((allowed) => allowed.value === option.value))
                  .map((option) => {
                    const selected = catalogOriginChosen && form.kind === option.value;
                    const Icon = option.icon;
                    return (
                      <Grid item xs={12} sm={6} key={option.value}>
                        <Paper
                          component="button"
                          type="button"
                          onClick={() => chooseKind(option.value)}
                          elevation={0}
                          aria-pressed={selected}
                          sx={{
                            width: "100%",
                            minHeight: 112,
                            p: 1.35,
                            textAlign: "left",
                            cursor: "pointer",
                            borderRadius: 2.5,
                            border: `2px solid ${selected ? ERP_BRAND.orange : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)"}`,
                            bgcolor: selected ? "rgba(243,90,58,.09)" : "var(--wp-panel-surface, #fff)",
                            boxShadow: selected ? "0 10px 26px rgba(243,90,58,.14)" : "none",
                            transition: "transform .15s ease, border-color .15s ease, box-shadow .15s ease",
                            "&:hover": { transform: "translateY(-1px)", borderColor: selected ? ERP_BRAND.orange : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.28)" },
                          }}
                        >
                          <Stack direction="row" spacing={1.1} alignItems="flex-start">
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: 2,
                                flex: "0 0 38px",
                                display: "grid",
                                placeItems: "center",
                                bgcolor: selected ? ERP_BRAND.orange : "rgba(243,90,58,.10)",
                                color: selected ? "#17181C" : ERP_BRAND.orange,
                              }}
                            >
                              <Icon fontSize="small" />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="space-between">
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 950, color: ERP_BRAND.orange, textTransform: "uppercase", letterSpacing: ".05em" }}>
                                    {option.label}
                                  </Typography>
                                  <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.1 }}>
                                    {option.title}
                                  </Typography>
                                </Box>
                                {selected ? <TaskAltRoundedIcon sx={{ color: ERP_BRAND.orange, fontSize: 22 }} /> : null}
                              </Stack>
                              <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.5, lineHeight: 1.3 }}>
                                {option.detail}
                              </Typography>
                              <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.dark, mt: 0.55, fontWeight: 800, lineHeight: 1.25 }}>
                                {option.hint}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    );
                  })}
              </Grid>
            </Paper>
          ) : null}

          {isStep("kind") && !catalogStockFlow ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step="1"
                title="¿Qué pasó?"
                description="Elegí la frase más parecida. El resto del formulario se arma solo."
              />
              <Grid container spacing={0.9}>
                {allowedKinds.map((option) => {
                  const selected = form.kind === option.value;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={option.value}>
                      <Paper
                        component="button"
                        type="button"
                        onClick={() => chooseKind(option.value)}
                        elevation={0}
                        aria-pressed={selected}
                        sx={{
                          width: "100%",
                          minHeight: 66,
                          p: 1.15,
                          textAlign: "left",
                          cursor: "pointer",
                          borderRadius: 2.2,
                          border: `2px solid ${selected ? ERP_BRAND.orange : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)"}`,
                          bgcolor: selected ? "rgba(243,90,58,.09)" : "var(--wp-panel-surface, #fff)",
                          boxShadow: selected ? "0 8px 20px rgba(243,90,58,.12)" : "none",
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.15 }}>{option.label}</Typography>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{option.detail}</Typography>
                          </Box>
                          {selected ? <TaskAltRoundedIcon sx={{ color: ERP_BRAND.orange, fontSize: 20 }} /> : null}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          ) : null}

          {isStep("description") && !catalogStockFlow ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title="Contalo en una frase"
                description="No hace falta escribir mucho. Una frase clara alcanza para reconocer este movimiento después."
              />
              <TextField
                fullWidth
                required
                autoFocus
                label="¿Qué pasó exactamente?"
                placeholder="Ej.: Venta de mostrador, compra semanal, pago de luz..."
                value={form.description}
                onChange={(event) => updateField("description", event.target.value)}
                helperText="Pensalo como el título del movimiento."
              />
            </Paper>
          ) : null}

          {isStep("timing") && !catalogStockFlow ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title="¿Cuándo pasó?"
                description="Ya viene cargado con este momento. Cambialo sólo si estás anotando algo anterior."
              />
              <TextField
                fullWidth
                label="Fecha y hora"
                type="datetime-local"
                value={form.occurredAt}
                onChange={(event) => updateField("occurredAt", event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Paper>
          ) : null}

          {isStep("document") ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title={catalogStockFlow && form.kind === "purchase" ? "¿Tenés factura o comprobante?" : "¿Hay un comprobante?"}
                description={catalogStockFlow && form.kind === "purchase"
                  ? "Si todavía no lo tenés, omití esta sección. La compra puede registrarse igual."
                  : "Factura, ticket, transferencia, número de pedido o cualquier referencia que te ayude a encontrarlo."}
              />
              <TextField
                fullWidth
                autoFocus
                label={catalogStockFlow && form.kind === "purchase" ? "Factura / comprobante" : "Comprobante o referencia"}
                placeholder={catalogStockFlow && form.kind === "purchase" ? "Ej.: Factura A 0001-00001234" : "Ej.: Ticket 1832, pedido #450, transferencia..."}
                value={form.sourceNumber}
                onChange={(event) => updateField("sourceNumber", event.target.value)}
              />
            </Paper>
          ) : null}

          {isStep("details") && catalogStockFlow ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title="Detalle del movimiento"
                description="WebProfit ya completó una descripción segura. Tocala sólo si querés dejar algo más específico."
              />
              <Grid container spacing={1.15}>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    label="Motivo / descripción"
                    value={form.description}
                    onChange={(event) => updateField("description", event.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="Fecha y hora"
                    type="datetime-local"
                    value={form.occurredAt}
                    onChange={(event) => updateField("occurredAt", event.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nota interna (opcional)"
                    value={form.notes}
                    onChange={(event) => updateField("notes", event.target.value)}
                  />
                </Grid>
              </Grid>
            </Paper>
          ) : null}

          {requiresParty && (isStep("party") || (catalogStockFlow && isStep("supplier"))) ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title={catalogStockFlow && form.kind === "purchase" ? "¿A quién se lo compraste?" : ["sale", "collection"].includes(form.kind) ? "Cliente relacionado" : "Proveedor o persona relacionada"}
                description={catalogStockFlow && form.kind === "purchase"
                  ? "Si lo cargás, la compra queda vinculada. Si no, podés seguir igual."
                  : "Es opcional, pero ayuda a reconocer deudas, pagos y el historial de cada persona."}
              />
              {catalogStockFlow ? (
                <TextField
                  fullWidth
                  label="Proveedor (opcional)"
                  placeholder="Ej.: Distribuidora Centro"
                  value={form.partyName}
                  onChange={(event) => updateField("partyName", event.target.value)}
                  helperText="Podés dejarlo vacío y completar la compra igual."
                />
              ) : (
                <Grid container spacing={1.4}>
                  {customerMovement && canReadCustomers ? (
                    <Grid item xs={12}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                        <Autocomplete
                          fullWidth
                          loading={loadingCustomers}
                          options={customers}
                          value={customers.find((customer) => customerIdOf(customer) === form.partyId) || null}
                          getOptionLabel={customerOptionLabel}
                          isOptionEqualToValue={(option, value) => customerIdOf(option) === customerIdOf(value)}
                          onChange={(_, customer) => selectCustomer(customer)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Buscar cliente del CRM"
                              placeholder="Nombre, documento, email o teléfono"
                              helperText={form.partyId ? "Este movimiento quedará vinculado a la ficha del CRM." : "Elegí uno existente o completá los datos para crear uno nuevo al guardar."}
                            />
                          )}
                        />
                        {form.partyId ? (
                          <Button variant="outlined" onClick={() => selectCustomer(null)} sx={{ whiteSpace: "nowrap" }}>
                            Nuevo cliente
                          </Button>
                        ) : null}
                      </Stack>
                    </Grid>
                  ) : null}
                  <Grid item xs={12} md={4}><TextField fullWidth label="Nombre" value={form.partyName} disabled={customerMovement && Boolean(form.partyId)} onChange={(event) => updateField("partyName", event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={2.5}><TextField fullWidth label="Documento" value={form.partyDocument} disabled={customerMovement && Boolean(form.partyId)} onChange={(event) => updateField("partyDocument", event.target.value)} /></Grid>
                  <Grid item xs={12} sm={6} md={3}><TextField fullWidth label="Email" value={form.partyEmail} disabled={customerMovement && Boolean(form.partyId)} onChange={(event) => updateField("partyEmail", event.target.value)} /></Grid>
                  <Grid item xs={12} md={2.5}><TextField fullWidth label="Teléfono" value={form.partyPhone} disabled={customerMovement && Boolean(form.partyId)} onChange={(event) => updateField("partyPhone", event.target.value)} /></Grid>
                  {customerMovement && !form.partyId && canManageCustomers ? (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                        Si completás nombre y al menos documento, email o teléfono, WebProfit creará la ficha en el CRM al guardar el movimiento.
                      </Typography>
                    </Grid>
                  ) : null}
                </Grid>
              )}
            </Paper>
          ) : null}

          {usesItems && isItemMicroStep ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title={isStep("item")
                  ? "¿Qué ítem intervino?"
                  : isStep("quantity")
                    ? catalogStockFlow
                      ? form.kind === "purchase"
                        ? "¿Cuánto compraste?"
                        : form.kind === "opening_balance"
                          ? "¿Cuánto stock ya tenías?"
                          : form.kind === "stock_adjustment"
                            ? "¿Qué diferencia encontraste?"
                            : "¿Cuánto stock ingresó?"
                      : "¿Qué cantidad se movió?"
                    : isStep("value")
                      ? form.kind === "sale" ? "¿A qué precio se vendió?" : form.kind === "opening_balance" ? "¿Qué costo de referencia tenía?" : "¿A qué costo entró?"
                      : isStep("receipt")
                        ? "¿La mercadería ya llegó?"
                        : "¿Qué cuenta representa esta operación?"}
                description={isStep("item")
                  ? form.kind === "purchase"
                    ? "Elegí el producto o escribí el concepto de la factura. Si todavía no conocés la variante, podés dejarla pendiente de identificar."
                    : "Buscá el ítem por nombre o código. Acá sólo elegimos qué fue; cantidad y valor vienen después."
                  : isStep("quantity")
                    ? stockAdjustment
                      ? "Usá + para sumar o - para descontar. Si hay variantes, cargá cada una por separado."
                      : "Cargá únicamente unidades y variantes. El precio o costo se completa en el siguiente micropaso."
                    : isStep("value")
                      ? "Este valor define el total del movimiento. No estamos moviendo dinero todavía; eso se registra después."
                      : isStep("receipt")
                        ? "Marcá sólo lo que todavía no llegó físicamente. Si ya lo tenés, dejalo como ‘entra al stock al confirmar’."
                        : "Es opcional y contable. Si no sabés qué elegir, dejá la predeterminada."}
                action={!catalogStockFlow && isStep("item") ? (
                  <Button
                    startIcon={<AddRoundedIcon />}
                    onClick={addItem}
                    variant="outlined"
                    sx={{ textTransform: "none", fontWeight: 900, alignSelf: { xs: "stretch", sm: "auto" } }}
                  >
                    Agregar ítem
                  </Button>
                ) : null}
              />
              {form.kind === "purchase" && isStep("item") ? (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Podés registrar la factura aunque todavía no conozcas color o talle. Marcá “Todavía no sé la variante” y completá lo que figure en el comprobante; WebProfit no inventará stock y te pedirá identificarlo cuando llegue.
                </Alert>
              ) : null}
              <Stack spacing={1.3}>
                {form.items.map((item, index) => {
                  const variantData = variantsForItem(item);
                  const distributedVariants = !item.stockIdentificationPending && usesVariantDistribution(item, variantData.variants);
                  const distributedTotal = distributedVariants ? distributedQuantity(item) : safeNumber(item.quantity);
                  const activeAccounts = (bootstrap.accounts || []).filter(
                    (account) => account.active !== false && account.metadata?.source !== "cash_register",
                  );
                  const accountTypes = form.kind === "sale"
                    ? new Set(["income"])
                    : form.kind === "opening_balance"
                      ? new Set(["asset"])
                      : form.kind === "purchase" && item.tracksStock
                        ? new Set(["asset"])
                        : new Set(["expense", "asset"]);
                  const accountOptions = activeAccounts.filter((account) => accountTypes.has(account.type));
                  const defaultAccountCode = form.kind === "sale"
                    ? variantData.product?.accounting?.salesAccountCode || "SALES"
                    : form.kind === "purchase"
                      ? item.tracksStock
                        ? variantData.product?.accounting?.inventoryAccountCode || "INVENTORY"
                        : variantData.product?.accounting?.purchaseAccountCode || "PURCHASES"
                      : form.kind === "expense"
                        ? variantData.product?.accounting?.expenseAccountCode || variantData.product?.accounting?.purchaseAccountCode || "OTHER_EXPENSE"
                        : form.kind === "opening_balance"
                          ? variantData.product?.accounting?.inventoryAccountCode || "INVENTORY"
                          : "";
                  const defaultAccount = activeAccounts.find((account) => account.code === defaultAccountCode);
                  if (isStep("receipt") && (!item.tracksStock || item.stockIdentificationPending || distributedVariants)) return null;
                  return (
                    <Box
                      key={`${index}-${item.productId}`}
                      sx={{
                        p: { xs: 1.25, sm: 1.7 },
                        borderRadius: 2.5,
                        bgcolor: ERP_BRAND.soft,
                        border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)",
                      }}
                    >
                      {!isStep("item") && !(catalogStockFlow && isStep("quantity")) ? (
                        <Stack
                          direction="row"
                          spacing={0.8}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 1 }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.15 }}>
                              {item.name || `Ítem ${index + 1}`}
                            </Typography>
                            {item.code ? (
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                                {item.code}
                              </Typography>
                            ) : null}
                          </Box>
                          <Chip
                            size="small"
                            label={`Ítem ${index + 1} de ${form.items.length}`}
                            variant="outlined"
                            sx={{ fontWeight: 850, flex: "0 0 auto" }}
                          />
                        </Stack>
                      ) : null}
                      <Grid container spacing={1.2} alignItems="center">
                        {form.kind === "purchase" && !catalogStockFlow && isStep("item") ? (
                          <Grid item xs={12}>
                            <Paper
                              elevation={0}
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderRadius: 2,
                                border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
                                bgcolor: item.stockIdentificationPending ? "rgba(237,108,2,.08)" : "var(--wp-panel-surface, #fff)",
                              }}
                            >
                              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} justifyContent="space-between">
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Switch
                                    size="small"
                                    color="warning"
                                    checked={item.stockIdentificationPending === true}
                                    onChange={(event) => setIdentificationPending(index, event.target.checked)}
                                    inputProps={{ "aria-label": `Variante pendiente de identificar para ${item.name || `ítem ${index + 1}`}` }}
                                  />
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                                      Todavía no sé la variante
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: ERP_BRAND.muted, lineHeight: 1.35 }}>
                                      Usalo cuando la factura trae modelo o código, pero todavía no sabés qué colores o talles llegaron.
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Chip
                                  size="small"
                                  color={item.stockIdentificationPending ? "warning" : "default"}
                                  variant={item.stockIdentificationPending ? "filled" : "outlined"}
                                  label={item.stockIdentificationPending ? "No genera stock" : "Ítem identificado"}
                                  sx={{ fontWeight: 900, alignSelf: { xs: "flex-start", sm: "center" } }}
                                />
                              </Stack>
                            </Paper>
                          </Grid>
                        ) : null}
                        {!item.stockIdentificationPending && (isStep("item") || (catalogStockFlow && isStep("quantity"))) ? (
                        <Grid item xs={12} md={3.5}>
                          <Stack spacing={0.7}>
                            {catalogStockFlow && item.productId ? (
                              <Paper
                                elevation={0}
                                sx={{
                                  px: 1.15,
                                  py: 0.9,
                                  borderRadius: 2,
                                  bgcolor: "var(--wp-panel-surface, #fff)",
                                  border: "1px solid rgba(243,90,58,.28)",
                                }}
                              >
                                <Stack direction="row" spacing={0.9} alignItems="center">
                                  <Box sx={{ width: 32, height: 32, borderRadius: 1.6, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.10)", color: ERP_BRAND.orange }}>
                                    <Inventory2RoundedIcon fontSize="small" />
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.1 }}>
                                      {item.name || projectProductName(variantData.product) || "Ítem seleccionado"}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                                      Stock actual: {projectProductStock(variantData.product)}{item.code ? ` · ${item.code}` : ""}
                                    </Typography>
                                  </Box>
                                </Stack>
                              </Paper>
                            ) : (
                              <AsyncProductAutocomplete
                                enabled={open}
                                projectId={projectId}
                                kind={form.kind}
                                value={products.find((candidate) => entityId(candidate) === item.productId) || (item.productId ? {
                                  _id: item.productId,
                                  id: item.productId,
                                  nombre: item.name || "Ítem seleccionado",
                                  name: item.name || "Ítem seleccionado",
                                  itemKind: item.itemType || "product",
                                  operational: { stockTracked: item.tracksStock === true },
                                } : null)}
                                onChange={(product) => selectProduct(index, product)}
                                onProductsLoaded={rememberProducts}
                                productCapabilities={productCapabilities}
                                isProductSelectable={productSelectableForCurrentKind}
                                requiresProduct={requiresStockProduct}
                              />
                            )}
                            {!catalogStockFlow ? (
                              <Button
                                type="button"
                                size="small"
                                variant="outlined"
                                startIcon={<QrCodeScannerRoundedIcon />}
                                onClick={() => setScannerItemIndex(index)}
                                sx={{ textTransform: "none", fontWeight: 900, alignSelf: "flex-start" }}
                              >
                                Escanear código
                              </Button>
                            ) : null}
                          </Stack>
                        </Grid>
                        ) : null}
                        {item.stockIdentificationPending && isStep("item") ? (
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label="Código de factura / proveedor"
                              value={item.code || ""}
                              onChange={(event) => updateItem(index, { code: event.target.value })}
                              helperText="Copialo tal como aparece en el comprobante."
                            />
                          </Grid>
                        ) : null}
                        {!item.stockIdentificationPending && variantData.variants.length && !distributedVariants && (isStep("item") || (catalogStockFlow && isStep("quantity"))) ? (
                          <Grid item xs={12} md={2.6}>
                            <TextField select fullWidth required label="Variante" value={item.variantId || ""} onChange={(event) => selectVariant(index, event.target.value)}>
                              <MenuItem value="">Elegí una variante</MenuItem>
                              {variantData.variants.map((variant) => (
                                <MenuItem key={variantIdOf(variant)} value={variantIdOf(variant)}>
                                  {variantLabelOf(variantData.product, variant) || variant.sku || "Variante"} · stock {safeNumber(variant.stock)}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        ) : null}
                        {!catalogStockFlow && isStep("item") ? (
                          <Grid item xs={12} md={item.stockIdentificationPending ? 3 : (stockAdjustment ? 2 : 2.5)}>
                            <TextField
                              fullWidth
                              label={item.stockIdentificationPending ? "Descripción de la factura" : "Concepto"}
                              value={item.name}
                              disabled={Boolean(item.productId)}
                              onChange={(event) => updateItem(index, { name: event.target.value })}
                              helperText={item.stockIdentificationPending ? "Ej.: Camisaco Volcom modelo X." : undefined}
                            />
                          </Grid>
                        ) : null}
                        {isStep("quantity") ? (
                        <Grid item xs={12} sm={6} md={stockAdjustment ? 5 : 4}>
                          {stockAdjustment ? (
                            <Stack spacing={0.35}>
                              <Stack direction="row" spacing={0.65} alignItems="center">
                                <Button
                                  type="button"
                                  variant="outlined"
                                  aria-label="Restar una unidad a la corrección"
                                  onClick={() => stepAdjustmentDelta(index, -1)}
                                  sx={{ minWidth: 50, height: 56, px: 1, fontWeight: 950 }}
                                >
                                  -1
                                </Button>
                                <TextField
                                  fullWidth
                                  type="number"
                                  label="Variación"
                                  value={item.adjustmentDelta}
                                  onChange={(event) => updateItem(index, { adjustmentDelta: event.target.value })}
                                />
                                <Button
                                  type="button"
                                  variant="outlined"
                                  aria-label="Sumar una unidad a la corrección"
                                  onClick={() => stepAdjustmentDelta(index, 1)}
                                  sx={{ minWidth: 50, height: 56, px: 1, fontWeight: 950 }}
                                >
                                  +1
                                </Button>
                              </Stack>
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted, pl: 0.25 }}>
                                + suma stock / - resta stock
                              </Typography>
                            </Stack>
                          ) : distributedVariants ? (
                            <TextField
                              fullWidth
                              label="Cantidad total"
                              value={String(distributedTotal)}
                              InputProps={{ readOnly: true }}
                              helperText="Suma de variantes"
                            />
                          ) : (
                            <TextField
                              fullWidth
                              type="number"
                              label="Cantidad"
                              autoFocus={catalogStockFlow && index === 0}
                              value={item.quantity}
                              onChange={(event) => updateItem(index, { quantity: event.target.value })}
                            />
                          )}
                        </Grid>
                        ) : null}
                        {isStep("value") && !stockAdjustment && !manualStockMove ? (
                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth
                              type="number"
                              autoFocus
                              label={form.kind === "sale" ? "Precio" : "Costo"}
                              value={form.kind === "sale" ? item.unitPrice : item.unitCost}
                              onChange={(event) => updateItem(index, form.kind === "sale" ? { unitPrice: event.target.value } : { unitCost: event.target.value })}
                            />
                          </Grid>
                        ) : null}
                        {isStep("value") && !stockAdjustment && !manualStockMove ? (
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ px: { xs: 0.5, md: 0 } }}>
                              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Subtotal</Typography>
                              <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{formatMoney(lineTotal(item, form.kind))}</Typography>
                            </Box>
                          </Grid>
                        ) : null}
                        {isStep("quantity") && distributedVariants ? (
                          <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, bgcolor: "var(--wp-panel-surface, #fff)", borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)" }}>
                              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={0.7} sx={{ mb: 1.1 }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Cantidad por variante</Typography>
                                  <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Cada valor impacta únicamente el stock de esa variante.</Typography>
                                </Box>
                                <Chip size="small" label={`${distributedTotal} unidad${distributedTotal === 1 ? "" : "es"} en total`} sx={{ fontWeight: 900 }} />
                              </Stack>
                              <Grid container spacing={1}>
                                {variantData.variants.map((variant) => {
                                  const currentVariantId = variantIdOf(variant);
                                  const variantQuantity = item.variantQuantities?.[currentVariantId] ?? "";
                                  const pendingReceipt = item.variantReceiptPending?.[currentVariantId] === true;
                                  return (
                                    <Grid item xs={12} sm={6} lg={4} key={currentVariantId}>
                                      <Box sx={{ p: 1, borderRadius: 2, bgcolor: ERP_BRAND.soft, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)" }}>
                                        <Typography variant="body2" sx={{ fontWeight: 900, color: ERP_BRAND.dark, mb: 0.2 }}>
                                          {variantLabelOf(variantData.product, variant) || variant.sku || "Variante"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted, display: "block", mb: 0.8 }}>
                                          Stock actual: {safeNumber(variant.stock)}
                                        </Typography>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          type="number"
                                          label="Cantidad"
                                          value={variantQuantity}
                                          onChange={(event) => updateVariantQuantity(index, currentVariantId, event.target.value)}
                                          inputProps={{ min: 0, step: "1" }}
                                        />
                                        {form.kind === "purchase" && !catalogStockFlow && safeNumber(variantQuantity) > 0 ? (
                                          <Button
                                            type="button"
                                            size="small"
                                            variant={pendingReceipt ? "contained" : "outlined"}
                                            color={pendingReceipt ? "warning" : "success"}
                                            onClick={() => updateVariantReceiptPending(index, currentVariantId, !pendingReceipt)}
                                            sx={{ mt: 0.75, width: "100%", textTransform: "none", fontWeight: 850 }}
                                          >
                                            {pendingReceipt ? "Esperando llegada" : "Recibir al confirmar"}
                                          </Button>
                                        ) : null}
                                      </Box>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Paper>
                          </Grid>
                        ) : null}
                        {isStep("account") && canChooseAccountingAccount && !catalogStockFlow && !stockAdjustment && ["sale", "purchase", "expense", "opening_balance"].includes(form.kind) ? (
                          <Grid item xs={12} md={4}>
                            <TextField
                              select
                              fullWidth
                              label="Cuenta contable de la operación"
                              value={item.accountingAccountCode || ""}
                              onChange={(event) => updateItem(index, { accountingAccountCode: event.target.value })}
                              helperText="La caja indica dónde está la plata; esta cuenta indica qué representa la operación."
                            >
                              <MenuItem value="">
                                Predeterminada: {defaultAccount?.name || defaultAccountCode || "configuración del ítem"}
                              </MenuItem>
                              {accountOptions.map((account) => (
                                <MenuItem key={account.code} value={account.code}>
                                  {account.name} · {account.code}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        ) : null}
                        {isStep("receipt") && form.kind === "purchase" && !catalogStockFlow && item.tracksStock && !item.stockIdentificationPending && !distributedVariants ? (
                          <Grid item xs={12}>
                            <Paper
                              elevation={0}
                              sx={{
                                px: 1.25,
                                py: 1,
                                borderRadius: 2,
                                border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
                                bgcolor: item.stockReceiptPending ? "rgba(237,108,2,.06)" : "rgba(46,125,50,.04)",
                              }}
                            >
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ sm: "center" }}
                                justifyContent="space-between"
                              >
                                <Stack direction="row" spacing={0.75} alignItems="center">
                                  <Switch
                                    size="small"
                                    color="warning"
                                    checked={item.stockReceiptPending === true}
                                    onChange={(event) => updateItem(index, { stockReceiptPending: event.target.checked })}
                                    inputProps={{ "aria-label": `Esperando llegada para ${item.name || `ítem ${index + 1}`}` }}
                                  />
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                                      Esperando llegada
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: ERP_BRAND.muted, lineHeight: 1.35 }}>
                                      Activá esta marca si ya compraste la mercadería pero todavía no llegó físicamente.
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Chip
                                  size="small"
                                  color={item.stockReceiptPending ? "warning" : "success"}
                                  variant={item.stockReceiptPending ? "filled" : "outlined"}
                                  label={item.stockReceiptPending ? "No suma stock todavía" : "Entra al stock al confirmar"}
                                  sx={{ fontWeight: 900, alignSelf: { xs: "flex-start", sm: "center" } }}
                                />
                              </Stack>
                              {item.stockReceiptPending ? (
                                <Typography variant="caption" sx={{ display: "block", mt: 0.65, pl: { sm: 4.5 }, color: ERP_BRAND.muted }}>
                                  La compra, el costo y el pago quedan registrados ahora. Estas unidades recién pasan a stock disponible cuando uses “Recibir mercadería”.
                                </Typography>
                              ) : null}
                            </Paper>
                          </Grid>
                        ) : null}
                        {!catalogStockFlow && isStep("item") ? <Grid item xs={2} md="auto"><IconButton color="error" aria-label="Quitar ítem" disabled={form.items.length === 1} onClick={() => removeItem(index)}><DeleteOutlineRoundedIcon /></IconButton></Grid> : null}
                      </Grid>
                    </Box>
                  );
                })}
              </Stack>
              {openingInventory && (isStep("quantity") || isStep("value")) ? (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  Esta acción suma físicamente las unidades que ya existían antes de usar WebProfit y registra su valor como inventario inicial. No descuenta dinero de ninguna caja.
                </Alert>
              ) : null}
            </Paper>
          ) : null}

          {commercial && isStep("money") ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title={form.kind === "sale" ? "Cobro y caja" : "Pago y caja"}
                description={form.kind === "sale" ? "Indicá dónde recibiste el dinero. Lo que falte queda por cobrar." : "Indicá de dónde salió el dinero. Lo que falte queda por pagar."}
                action={(
                  <Button
                    startIcon={<AddRoundedIcon />}
                    onClick={addPayment}
                    disabled={!registers.length}
                    variant="outlined"
                    sx={{ textTransform: "none", fontWeight: 900, alignSelf: { xs: "stretch", sm: "auto" } }}
                  >
                    Agregar pago
                  </Button>
                )}
              />
              <Stack spacing={1.2}>
                {form.payments.map((payment, index) => {
                  const paymentRegister = registerById(payment.cashRegisterId);
                  const processing = paymentRegister?.paymentProcessing || {};
                  const settlement = paymentSettlementPreview(paymentRegister, payment);
                  const showSettlement = ["sale", "collection"].includes(form.kind)
                    && ["manual", "mercadopago"].includes(processing.provider);
                  return (
                    <Box key={index} sx={{ py: 1.1, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
                      <Grid container spacing={1.1} alignItems="center">
                        <Grid item xs={12} md={5}>
                          <TextField
                            select
                            fullWidth
                            label="¿Dónde entró el cobro?"
                            value={payment.cashRegisterId}
                            onChange={(event) => updatePayment(index, {
                              cashRegisterId: event.target.value,
                              installments: "1",
                              commissionAmount: "",
                              commissionTaxAmount: "",
                              financingCostAmount: "",
                              withholdingAmount: "",
                              providerPaymentId: "",
                              settlementSource: "",
                              settlementStatus: "",
                              settlementNetAmount: "",
                              providerGrossAmount: "",
                            })}
                          >
                            {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)} disabled={!registerIsUsable(register)}>{registerOptionLabel(register)}</MenuItem>)}
                          </TextField>
                        </Grid>
                        <Grid item xs={5} md={2}><TextField fullWidth type="number" label="Cobrado" value={payment.amount} onChange={(event) => updatePayment(index, { amount: event.target.value })} /></Grid>
                        <Grid item xs={5} md={4}><TextField fullWidth label="Referencia" value={payment.reference} onChange={(event) => updatePayment(index, { reference: event.target.value })} /></Grid>
                        <Grid item xs={2} md={1}><IconButton color="error" aria-label="Quitar pago" onClick={() => removePayment(index)}><DeleteOutlineRoundedIcon /></IconButton></Grid>
                      </Grid>

                      {showSettlement && processing.provider === "mercadopago" ? (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Mercado Pago: pegá el ID del pago para traer cuotas, descuentos y neto directamente desde la API antes de confirmar esta venta.
                        </Alert>
                      ) : null}

                      {showSettlement ? (
                        <Grid container spacing={1} sx={{ mt: .15 }} alignItems="center">
                          <Grid item xs={6} sm={3} md={2}>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              label="Cuotas"
                              value={payment.installments || "1"}
                              onChange={(event) => updatePayment(index, { installments: event.target.value, financingCostAmount: "" })}
                            >
                              {[1, 2, 3, 6, 9, 12, 18, 24].map((value) => <MenuItem key={value} value={String(value)}>{value}</MenuItem>)}
                            </TextField>
                          </Grid>
                          {processing.provider === "manual" ? (
                            <>
                              <Grid item xs={6} sm={3} md={2}>
                                <TextField size="small" fullWidth type="number" label="Comisión real" value={payment.commissionAmount} placeholder={String(settlement.commissionAmount || 0)} onChange={(event) => updatePayment(index, { commissionAmount: event.target.value })} />
                              </Grid>
                              <Grid item xs={6} sm={3} md={2}>
                                <TextField size="small" fullWidth type="number" label="Costo cuotas" value={payment.financingCostAmount} placeholder={String(settlement.financingCostAmount || 0)} onChange={(event) => updatePayment(index, { financingCostAmount: event.target.value })} />
                              </Grid>
                              <Grid item xs={6} sm={3} md={2}>
                                <TextField size="small" fullWidth type="number" label="Impuesto comisión" value={payment.commissionTaxAmount} placeholder={String(settlement.commissionTaxAmount || 0)} onChange={(event) => updatePayment(index, { commissionTaxAmount: event.target.value })} />
                              </Grid>
                              <Grid item xs={6} sm={3} md={2}>
                                <TextField size="small" fullWidth type="number" label="Retenciones" value={payment.withholdingAmount} onChange={(event) => updatePayment(index, { withholdingAmount: event.target.value })} />
                              </Grid>
                              <Grid item xs={6} sm={3} md={2}>
                                <Typography variant="caption" sx={{ color: ERP_BRAND.muted, display: "block" }}>Entra realmente</Typography>
                                <Typography sx={{ fontWeight: 950 }}>{formatMoney(settlement.netAmount, paymentRegister?.currency || "ARS")}</Typography>
                              </Grid>
                            </>
                          ) : (
                            <>
                              <Grid item xs={12} sm={7} md={6}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  label="ID del pago Mercado Pago"
                                  value={payment.providerPaymentId}
                                  onChange={(event) => updatePayment(index, {
                                    providerPaymentId: event.target.value,
                                    settlementSource: "",
                                    settlementStatus: "",
                                    settlementNetAmount: "",
                                    providerGrossAmount: "",
                                  })}
                                  placeholder="Pegalo si registrás esta venta manualmente"
                                />
                              </Grid>
                              <Grid item xs={12} sm={5} md={4}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  disabled={settlementLoadingIndex === index}
                                  onClick={() => fetchMercadoPagoSettlement(index)}
                                  sx={{ minHeight: 40, textTransform: "none", fontWeight: 900 }}
                                >
                                  {settlementLoadingIndex === index ? "Consultando…" : "Traer costos reales"}
                                </Button>
                              </Grid>
                              {payment.settlementSource === "provider" ? (
                                <Grid item xs={12} md={10}>
                                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ px: .2 }}>
                                    <Typography variant="body2"><strong>Comisión:</strong> {formatMoney(settlement.commissionAmount, paymentRegister?.currency || "ARS")}</Typography>
                                    <Typography variant="body2"><strong>Financiación:</strong> {formatMoney(settlement.financingCostAmount, paymentRegister?.currency || "ARS")}</Typography>
                                    <Typography variant="body2"><strong>Otros descuentos:</strong> {formatMoney(settlement.commissionTaxAmount, paymentRegister?.currency || "ARS")}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 950 }}><strong>Neto:</strong> {formatMoney(settlement.netAmount, paymentRegister?.currency || "ARS")}</Typography>
                                  </Stack>
                                </Grid>
                              ) : null}
                            </>
                          )}
                        </Grid>
                      ) : null}
                    </Box>
                  );
                })}
                {!form.payments.length ? <Alert severity="info">Sin pago registrado: el total quedará pendiente.</Alert> : null}
              </Stack>
            </Paper>
          ) : null}

          {manualMoney && isStep("money") ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title="Importe y caja"
                description="Seleccioná la caja o cuenta real para que el movimiento quede correctamente ubicado."
              />
              <Grid container spacing={1.4}>
                <Grid item xs={12} sm={5} md={4}><TextField fullWidth type="number" label="Importe" value={form.manualAmount} onChange={(event) => updateField("manualAmount", event.target.value)} /></Grid>
                <Grid item xs={12} sm={7} md={5}>
                  <TextField select fullWidth label={["collection", "cash_in"].includes(form.kind) ? "¿Dónde ingresó?" : "¿De dónde salió?"} value={form.moneyRegisterId} onChange={(event) => updateField("moneyRegisterId", event.target.value)}>
                    {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)} disabled={!registerIsUsable(register)}>{registerOptionLabel(register)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}><TextField fullWidth label="Referencia" value={form.paymentReference} onChange={(event) => updateField("paymentReference", event.target.value)} /></Grid>
              </Grid>
            </Paper>
          ) : null}

          {transferMode && isStep("money") ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title="Origen, destino e importe"
                description="El dinero sale de una caja o cuenta y entra en otra. Ambas deben usar la misma moneda."
              />
              <Grid container spacing={1.4}>
                <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Importe" value={form.manualAmount} onChange={(event) => updateField("manualAmount", event.target.value)} /></Grid>
                <Grid item xs={12} sm={6} md={4.5}>
                  <TextField select fullWidth label="Sale de" value={form.transferFromId} onChange={(event) => updateField("transferFromId", event.target.value)}>
                    {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)} disabled={!registerIsUsable(register)}>{registerOptionLabel(register)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6} md={4.5}>
                  <TextField select fullWidth label="Entra en" value={form.transferToId} onChange={(event) => updateField("transferToId", event.target.value)}>
                    {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)} disabled={!registerIsUsable(register)}>{registerOptionLabel(register)}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
            </Paper>
          ) : null}

          {isStep("notes") && !catalogStockFlow ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step={String(safeActiveStep + 1)}
                title="¿Querés dejar una nota?"
                description="Sólo para algo que quieras recordar después. Si no hace falta, omití esta sección."
              />
              <TextField
                fullWidth
                autoFocus
                multiline
                minRows={2}
                maxRows={3}
                label="Nota interna"
                placeholder="Ej.: entregar el viernes, falta factura, revisar diferencia..."
                value={form.notes}
                onChange={(event) => updateField("notes", event.target.value)}
              />
            </Paper>
          ) : null}

          {isStep("review") ? (
            <Paper elevation={0} sx={sectionSx}>
              <SectionHeading
                step="✓"
                title="Revisá antes de impactar"
                description={confirmationImpact}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mb: 1.35 }}>
                <Chip
                  icon={catalogStockFlow ? <Inventory2RoundedIcon /> : undefined}
                  label={catalogStockFlow
                    ? CATALOG_STOCK_KIND_OPTIONS.find((option) => option.value === form.kind)?.title || selectedKind?.label
                    : selectedKind?.label || form.kind}
                  sx={{ fontWeight: 900 }}
                />
                {usesItems && form.items[0]?.name ? (
                  <Chip label={form.items.length > 1 ? `${form.items[0].name} + ${form.items.length - 1}` : form.items[0].name} variant="outlined" sx={{ fontWeight: 850 }} />
                ) : null}
                {catalogStockFlow && usesItems ? (
                  <Chip
                    label={stockAdjustment
                      ? `Ajuste ${catalogQuantityPreview > 0 ? "+" : ""}${catalogQuantityPreview}`
                      : `${catalogQuantityPreview} unidad${Math.abs(catalogQuantityPreview) === 1 ? "" : "es"}`}
                    color={catalogQuantityPreview < 0 ? "warning" : "success"}
                    variant="outlined"
                    sx={{ fontWeight: 950 }}
                  />
                ) : null}
                {form.sourceNumber ? <Chip icon={<ReceiptLongRoundedIcon />} label={form.sourceNumber} variant="outlined" sx={{ fontWeight: 850 }} /> : null}
              </Stack>

              <Box
                sx={{
                  mt: 1.25,
                  p: { xs: 1.25, sm: 1.5 },
                  color: "#fff",
                  borderRadius: 2.5,
                  background: "linear-gradient(135deg,#19242F 0%,#30495B 100%)",
                }}
              >
                {catalogStockFlow && !["purchase", "opening_balance"].includes(form.kind) ? (
                  <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,.64)", fontWeight: 900 }}>IMPACTO EN STOCK</Typography>
                      <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950 }}>
                        {stockAdjustment
                          ? `${catalogQuantityPreview > 0 ? "+" : ""}${catalogQuantityPreview} unidades`
                          : `+${catalogQuantityPreview} unidades`}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,.72)", maxWidth: 390, textAlign: "right" }}>
                      Sin compra ni movimiento de dinero. Queda registrada la trazabilidad.
                    </Typography>
                  </Stack>
                ) : (
                  <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 850 }}>TOTAL</Typography>
                      <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950 }}>{formatMoney(total)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 850 }}>{form.kind === "sale" ? "COBRADO" : "PAGADO"}</Typography>
                      <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950 }}>{formatMoney(paid)}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 850 }}>PENDIENTE</Typography>
                      <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950 }}>{formatMoney(pending)}</Typography>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          flex: "0 0 auto",
          p: { xs: 1.2, sm: 2 },
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          bgcolor: "var(--wp-panel-surface, #fff)",
          borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
          boxShadow: "0 -10px 28px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.06)",
          "& .MuiButton-root": { minHeight: 42 },
        }}
      >
        <Button
          onClick={requestClose}
          disabled={saving}
          fullWidth={mobile}
          sx={{ textTransform: "none", fontWeight: 850, order: mobile ? 4 : 1 }}
        >
          Cancelar
        </Button>

        {safeActiveStep > 0 ? (
          <Button
            type="button"
            onClick={() => goToMovementStep(safeActiveStep - 1)}
            disabled={saving}
            startIcon={<ArrowBackRoundedIcon />}
            fullWidth={mobile}
            sx={{ textTransform: "none", fontWeight: 900, order: mobile ? 3 : 2 }}
          >
            Anterior
          </Button>
        ) : null}

        {activeStepMeta?.optional && safeActiveStep < movementSteps.length - 1 ? (
          <Button
            type="button"
            onClick={skipMovementStep}
            disabled={saving}
            fullWidth={mobile}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 950,
              color: ERP_BRAND.dark,
              borderColor: "rgba(243,90,58,.42)",
              bgcolor: "rgba(243,90,58,.045)",
              minWidth: { sm: 220 },
              order: mobile ? 2 : 3,
              "&:hover": { borderColor: ERP_BRAND.orange, bgcolor: "rgba(243,90,58,.09)" },
            }}
          >
            Omitir esta sección · es opcional
          </Button>
        ) : null}

        {isStep("review") && !catalogStockFlow ? (
          <Button
            variant="outlined"
            startIcon={saving ? <WebProfitSpinner size={16} /> : <SaveRoundedIcon />}
            disabled={saving}
            onClick={() => saveDraft()}
            fullWidth={mobile}
            sx={{ textTransform: "none", fontWeight: 900, order: mobile ? 2 : 4 }}
          >
            {saving ? "Guardando..." : "Guardar borrador"}
          </Button>
        ) : null}

        <Button
          variant="contained"
          startIcon={saving
            ? <WebProfitSpinner size={16} />
            : isStep("review") ? <TaskAltRoundedIcon /> : <ArrowForwardRoundedIcon />}
          disabled={saving || (isStep("review") && showMoneySetupWarning)}
          onClick={goToNextMovementStep}
          fullWidth={mobile}
          sx={{
            textTransform: "none",
            fontWeight: 950,
            bgcolor: ERP_BRAND.orange,
            order: mobile ? 1 : 5,
            minWidth: { sm: 170 },
            boxShadow: "0 8px 22px rgba(243,90,58,.20)",
            "&:hover": { bgcolor: "#D9472B", boxShadow: "0 10px 26px rgba(243,90,58,.26)" },
          }}
        >
          {saving
            ? "Guardando..."
            : isStep("review")
              ? catalogStockFlow ? "Revisar impacto y guardar" : "Guardar y revisar impacto"
              : "Siguiente"}
        </Button>
      </DialogActions>
    </Dialog>

      <Dialog
        open={open && Boolean(saveErrorDialogMessage)}
        onClose={saving ? undefined : () => setSaveErrorDialogMessage("")}
        fullWidth
        maxWidth="xs"
        aria-labelledby="movement-save-error-title"
        aria-describedby="movement-save-error-description"
      >
        <DialogTitle id="movement-save-error-title" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
          No se puede guardar todavía
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Hay un dato que necesitás corregir antes de guardar el borrador.
          </Alert>
          <Typography
            id="movement-save-error-description"
            sx={{ color: ERP_BRAND.dark, lineHeight: 1.55, fontWeight: 750 }}
          >
            {saveErrorDialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            autoFocus
            variant="contained"
            onClick={() => setSaveErrorDialogMessage("")}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            Entendido, corregir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open && discardConfirmOpen}
        onClose={saving ? undefined : () => setDiscardConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="movement-discard-title"
        aria-describedby="movement-discard-description"
      >
        <DialogTitle id="movement-discard-title" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
          Hay cambios sin guardar
        </DialogTitle>
        <DialogContent>
          <Typography id="movement-discard-description" sx={{ color: ERP_BRAND.muted, lineHeight: 1.55 }}>
            Si cerrás ahora, se van a perder los cambios que todavía no guardaste.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            gap: 1,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            "& > :not(style)": { m: 0, minHeight: 44 },
          }}
        >
          <Button
            autoFocus
            onClick={() => setDiscardConfirmOpen(false)}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 850 }}
          >
            Seguir editando
          </Button>
          <Button
            color="error"
            variant="outlined"
            onClick={closeImmediately}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            Descartar y cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmationOpen}
        onClose={saving ? undefined : () => setConfirmationOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="movement-confirmation-title"
      >
        <DialogTitle id="movement-confirmation-title" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
          Confirmar impacto del movimiento
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="success">
              El borrador {reviewMovement.movementNumber ? `${reviewMovement.movementNumber} ` : ""}quedó guardado.
              Todavía no modificó stock, caja ni contabilidad.
            </Alert>

            <Box>
              <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, mb: 0.6 }}>
                Esto ocurrirá al confirmar
              </Typography>
              <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mb: 1.4 }}>
                {confirmationImpact}
              </Typography>
              <Stack spacing={1}>
                {reviewFacts.map((fact) => (
                  <Box
                    key={fact}
                    sx={{
                      p: 1.3,
                      borderRadius: 2,
                      bgcolor: ERP_BRAND.soft,
                      border: `1px solid ${ERP_BRAND.border}`,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 750, color: ERP_BRAND.dark }}>
                      {fact}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip label={`Total ${formatMoney(reviewTotal, reviewCurrency)}`} />
              <Chip
                label={`Registrado ${formatMoney(reviewPaid, reviewCurrency)}`}
                color={reviewPaid > 0 ? "success" : "default"}
                variant={reviewPaid > 0 ? "filled" : "outlined"}
              />
              <Chip
                label={`Pendiente ${formatMoney(reviewPending, reviewCurrency)}`}
                color={reviewPending > 0 ? "warning" : "success"}
                variant="outlined"
              />
            </Stack>

            {form.kind === "sale" ? (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2.4,
                  border: "1px solid rgba(25,36,47,.12)",
                  bgcolor: "rgba(25,36,47,.025)",
                }}
              >
                <Stack spacing={1.2}>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Facturación fiscal</Typography>
                    <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                      La venta y la factura son dos estados distintos. Elegí qué querés hacer después de confirmar la venta.
                    </Typography>
                  </Box>

                  <Grid container spacing={1}>
                    {[
                      {
                        value: "pending",
                        title: "No emitir ahora",
                        detail: "Confirma la venta y la deja en Facturación → Ventas pendientes.",
                      },
                      {
                        value: "issue",
                        title: "Facturar esta venta",
                        detail: "Confirma la venta y solicita el CAE a ARCA sin duplicar el movimiento.",
                      },
                    ].map((option) => {
                      const selected = fiscalAction === option.value;
                      return (
                        <Grid item xs={12} sm={6} key={option.value}>
                          <Paper
                            component="button"
                            type="button"
                            onClick={() => {
                              if (option.value === "issue" && !canOperateFiscal) return;
                              setFiscalAction(option.value);
                            }}
                            disabled={option.value === "issue" && !canOperateFiscal}
                            aria-pressed={selected}
                            elevation={0}
                            sx={{
                              width: "100%",
                              height: "100%",
                              p: 1.25,
                              textAlign: "left",
                              cursor: option.value === "issue" && !canOperateFiscal ? "not-allowed" : "pointer",
                              opacity: option.value === "issue" && !canOperateFiscal ? 0.58 : 1,
                              borderRadius: 2,
                              border: `2px solid ${selected ? ERP_BRAND.orange : "rgba(25,36,47,.10)"}`,
                              bgcolor: selected ? "rgba(243,90,58,.07)" : "#fff",
                            }}
                          >
                            <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{option.title}</Typography>
                            <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                              {option.value === "issue" && !canOperateFiscal
                                ? "Tu rol deja la venta pendiente para el equipo de Facturación."
                                : option.detail}
                            </Typography>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {fiscalAction === "issue" ? (
                    <Stack spacing={1}>
                      <Alert severity="info" icon={<ReceiptLongRoundedIcon fontSize="inherit" />}>
                        Si ARCA no responde, la venta no se pierde ni se duplica: WebProfit conserva el comprobante para conciliación o reintento.
                      </Alert>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={7}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Cliente / razón social"
                            value={fiscalRecipient.name}
                            onChange={(event) => setFiscalRecipient((current) => ({ ...current, name: event.target.value }))}
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            size="small"
                            select
                            fullWidth
                            label="Condición IVA"
                            value={fiscalRecipient.taxConditionCode}
                            onChange={(event) => setFiscalRecipient((current) => ({ ...current, taxConditionCode: Number(event.target.value) }))}
                          >
                            <MenuItem value={5}>Consumidor Final</MenuItem>
                            <MenuItem value={1}>IVA Responsable Inscripto</MenuItem>
                            <MenuItem value={6}>Responsable Monotributo</MenuItem>
                            <MenuItem value={4}>IVA Exento</MenuItem>
                            <MenuItem value={7}>Sujeto No Categorizado</MenuItem>
                            <MenuItem value={13}>Monotributista Social</MenuItem>
                            <MenuItem value={15}>IVA No Alcanzado</MenuItem>
                            <MenuItem value={16}>Monotributo Trabajador Independiente Promovido</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            size="small"
                            fullWidth
                            label="CUIT / CUIL / DNI"
                            value={fiscalRecipient.documentNumber}
                            onChange={(event) => setFiscalRecipient((current) => ({ ...current, documentNumber: event.target.value.replace(/\D+/g, "").slice(0, 11) }))}
                            helperText={[1, 6, 13, 16].includes(Number(fiscalRecipient.taxConditionCode)) ? "Para este receptor se requiere CUIT/CUIL de 11 dígitos." : "Puede quedar vacío para Consumidor Final cuando corresponda."}
                          />
                        </Grid>
                        <Grid item xs={12} sm={7}>
                          <TextField
                            size="small"
                            fullWidth
                            label="Domicilio fiscal (opcional)"
                            value={fiscalRecipient.address}
                            onChange={(event) => setFiscalRecipient((current) => ({ ...current, address: event.target.value }))}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  ) : null}
                </Stack>
              </Paper>
            ) : null}

            <Alert severity="warning">
              Confirmar aplica estos cambios y bloquea la edición del movimiento. Una corrección posterior deberá hacerse mediante una reversa.
            </Alert>
            {!canConfirm ? (
              <Alert severity="info">
                El borrador quedó guardado, pero tu rol no tiene permiso para confirmarlo.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={() => setConfirmationOpen(false)}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 850 }}
          >
            Volver a editar
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setConfirmationOpen(false);
              closeImmediately();
            }}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 900 }}
          >
            Dejar como borrador
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={saving ? <WebProfitSpinner size={16} /> : <TaskAltRoundedIcon />}
            onClick={confirmDraft}
            disabled={saving || !canConfirm}
            sx={{ textTransform: "none", fontWeight: 950 }}
          >
            {saving
              ? fiscalAction === "issue" && form.kind === "sale" ? "Confirmando y facturando..." : "Confirmando..."
              : fiscalAction === "issue" && form.kind === "sale" ? "Confirmar venta y facturar" : "Confirmar e impactar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open && Boolean(fiscalResult)}
        onClose={saving ? undefined : () => { setFiscalResult(null); closeImmediately(); }}
        fullWidth
        maxWidth="sm"
        aria-labelledby="movement-fiscal-result-title"
      >
        <DialogTitle id="movement-fiscal-result-title" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
          {fiscalResult?.status === "authorized"
            ? "Venta confirmada y factura autorizada"
            : fiscalResult?.status === "pending"
              ? "Venta confirmada · ARCA en conciliación"
              : "Venta confirmada · factura pendiente"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.4}>
            <Alert severity={fiscalResult?.status === "error" ? "warning" : "success"}>
              {fiscalResult?.message}
            </Alert>
            {fiscalResult?.displayNumber || fiscalResult?.cae ? (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {fiscalResult.displayNumber ? <Chip label={`Comprobante ${fiscalResult.displayNumber}`} /> : null}
                {fiscalResult.cae ? <Chip color="success" label={`CAE ${fiscalResult.cae}`} /> : null}
              </Stack>
            ) : null}
            <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
              La venta ya impactó stock, caja y contabilidad. La facturación conserva su propio estado para evitar dobles ventas o comprobantes duplicados.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={() => { setFiscalResult(null); closeImmediately(); }}
            sx={{ textTransform: "none", fontWeight: 850 }}
          >
            Cerrar
          </Button>
          <Button
            variant="contained"
            startIcon={<ReceiptLongRoundedIcon />}
            onClick={() => {
              setFiscalResult(null);
              closeImmediately();
              navigate(`/dashboard/proyectos/${projectId}/sistema/facturacion`);
            }}
            sx={{ textTransform: "none", fontWeight: 950 }}
          >
            Ir a Facturación
          </Button>
        </DialogActions>
      </Dialog>

      <BarcodeScannerDialog
        open={Number.isInteger(scannerItemIndex)}
        onClose={() => setScannerItemIndex(null)}
        onScan={scanProductIntoLine}
        title="Escanear ítem para el movimiento"
        helper="Escaneá el código del ítem, SKU o barcode de una variante. Si el código identifica una variante, WebProfit la selecciona automáticamente."
      />
    </>
  );
}
